import ResponsiveContainer from "../components/DensityResponsiveContainer";
/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea, 
  ReferenceDot,
  ReferenceLine,

  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import * as d3 from "d3"; // <<< NEW: use robust CSV parser
import { GameDetailSkeleton } from "../components/skeletons/GameDetailSkeleton";
import { SkeletonChart } from "../components/skeletons/SkeletonLoader";
import { useGlobalData } from "../contexts/DataContext";
import { useTikTokData } from "../contexts/TikTokDataContext";
import VirtualizedDropdownAsync from "../components/VirtualizedDropdown/VirtualizedDropdownAsync";

/* ------------------------------------------------------------------ */
import { API_BASE } from "../config/api";
const DEFAULT_BASES = [API_BASE];

// mindSHARE source
const GAME_STATS_URL =
  "https://d1ct3zodbaiefe.cloudfront.net/game_stats.csv.gz";

// North America prime time (UTC 23:00 → 04:00 next day)
const NA_UTC_START_HOUR = 6;
const NA_UTC_END_HOUR   = 11;                           // wraps past midnight
const NA_BAND_FILL      = "rgba(63, 169, 245, 0.16)";  // blue

// Asia prime time (UTC 11:00 → 15:00; ~7–11pm China, 8–12pm JP/KR)
const ASIA_UTC_START_HOUR = 18;
const ASIA_UTC_END_HOUR   = 23;
const ASIA_BAND_FILL      = "rgba(255, 235, 59, 0.1)"; // soft, barely-yellow

// Europe prime time (UTC 17:00 → 21:00)
const EU_UTC_START_HOUR = 0;
const EU_UTC_END_HOUR   = 5;
const EU_BAND_FILL      = "rgba(76, 175, 80, 0.14)";   // green

// Prefer arrayBuffer + DecompressionStream to avoid creating a 800MB JS string.
async function fetchGzipText(url) {
  const res = await fetch(url, { mode: "cors", cache: "no-store" });
  if (!res.ok) throw new Error(`${url} HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  const u8  = new Uint8Array(buf);
  const looksGzip = u8.length >= 2 && u8[0] === 0x1f && u8[1] === 0x8b;
  if (looksGzip && "DecompressionStream" in window) {
    const ds = new DecompressionStream("gzip");
    const stream = new Response(u8).body.pipeThrough(ds);
    return (await new Response(stream).text()).trim();
  }
  // If CF already decompressed for us, this is just UTF-8
  return new TextDecoder("utf-8").decode(u8).trim();
}

async function fetchJSON(pathOrUrl) {
  const isFull = /^https?:\/\//i.test(pathOrUrl);
  const makeUrl = (base) =>
    isFull
      ? pathOrUrl
      : `${base}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;

  let lastErr = null;
  for (const base of DEFAULT_BASES) {
    try {
      const url = makeUrl(base);
      const r = await fetch(url, { cache: "no-store" });
      if (r.ok) return await r.json();
    } catch (e) {
      lastErr = e;
    }
  }
  if (lastErr) console.warn("fetchJSON fallbacks exhausted:", lastErr);
  return null;
}

const fmtDate = (dt) =>
  new Date(dt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
const fmtDateTime = (dt) =>
  new Date(dt).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const shortDateISO = (val) => {
  if (!val) return "";
  const m = String(val).match(/\d{4}-\d{2}-\d{2}/);
  if (m) return m[0];
  try {
    return new Date(val).toISOString().slice(0, 10);
  } catch {
    return String(val);
  }
};

// --- Twitch "last day" game ranks (computed from daily rollup) ---
// CloudFront base for Twitch data (match Twitch.jsx)
const CF_TWITCH = "https://d1dlav2y8bte65.cloudfront.net";

// --- Twitch "last day" game ranks (computed from daily rollup) ---
const TWITCH_DAILY_URL = `${CF_TWITCH}/daily_game_rollup.csv.gz`;

// Decompress .csv.gz in-browser if needed (works on S3 objects without Content-Encoding:gzip)
// --- plain text of .csv(.gz) ---
// robust: works for .csv and .csv.gz regardless of headers / encodings
async function fetchGzipCsvText(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`fetchGzipCsvText: ${url} → HTTP ${res.status}`);

  const buf = await res.arrayBuffer();
  const u8  = new Uint8Array(buf);

  // gzip magic bytes?
  const looksGzip = u8.length >= 2 && u8[0] === 0x1f && u8[1] === 0x8b;

  if (looksGzip && "DecompressionStream" in window) {
    try {
      const ds = new DecompressionStream("gzip");
      const stream = new Response(u8).body.pipeThrough(ds);
      return (await new Response(stream).text()).trim();
    } catch (e) {
      console.warn("fetchGzipCsvText: DS failed, falling back to UTF-8 decode", e);
      // fall through
    }
  }

  // Plain UTF-8 path (already decompressed upstream)
  return new TextDecoder("utf-8").decode(u8).trim();
}

// Simple CSV parse (quote-aware) using d3 (already imported)
function parseCsvObjects(csvText) {
  // d3.csvParse is quote-aware and handles embedded commas/newlines
  return d3.csvParse(csvText.trim());
}

/* ----------------------------- daily helpers ----------------------------- */
const dayKeyUTC = (dt) => {
  const d = new Date(dt);
  if (Number.isNaN(d)) return null;
  const y = d.getUTCFullYear(),
    m = d.getUTCMonth(),
    dd = d.getUTCDate();
  return new Date(Date.UTC(y, m, dd)).toISOString().slice(0, 10);
};
const startOfDayUTCISO = (dayStr) => `${dayStr}T00:00:00.000Z`;

function normalizeArchive(rows) {
  return (rows || [])
    .map((r) => ({
      dt: startOfDayUTCISO(dayKeyUTC(r.dt)),
      ccu: Number(r.ccu) || 0,
    }))
    .sort((a, b) => new Date(a.dt) - new Date(b.dt));
}
function dailyFromLiveSnapshots(rows) {
  const byDay = new Map();
  for (const r of rows || []) {
    const k = dayKeyUTC(r.dt);
    const v = Number(r.ccu);
    if (!k || !Number.isFinite(v) || v < 0) continue;
    const prev = byDay.get(k);
    if (prev === undefined || v > prev) byDay.set(k, v);
  }
  return Array.from(byDay.entries())
    .map(([day, max]) => ({ dt: startOfDayUTCISO(day), ccu: max }))
    .sort((a, b) => new Date(a.dt) - new Date(b.dt));
}

/* ------------------------------ live helpers ----------------------------- */
function forwardFill(series) {
  let last = null;
  for (let i = 0; i < series.length; i++) {
    const v = Number(series[i].ccu);
    if (Number.isFinite(v) && v > 0) {
      last = v;
    } else if (last != null) {
      series[i].ccu = last;
    }
  }
  return series;
}

function normalizeLive(rows) {
  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  const floor = now - sevenDaysMs;

  const out = [];
  for (const r of rows || []) {
    let d = new Date(r.dt);
    if (Number.isNaN(d)) {
      const coerce = String(r.dt).replace(" ", "T") + "Z";
      d = new Date(coerce);
    }
    const t = d.getTime();
    if (!Number.isFinite(t) || t < floor) continue;

    let v = Number(r.ccu);
    if (!(Number.isFinite(v) && v > 0)) v = NaN;

    out.push({ dt: d.toISOString(), ccu: v });
  }
  out.sort((a, b) => new Date(a.dt) - new Date(b.dt));
  return forwardFill(out);
}

async function loadLiveSeries(appId, hours = 24 * 7) {
  const live = await fetchJSON(`/live_ccu/${appId}?hours=${hours}`);
  if (Array.isArray(live) && live.length) return normalizeLive(live);
  return [];
}
async function loadDailySeries(appId) {
  const daily = await fetchJSON(`/ccu_daily/${appId}`);
  if (Array.isArray(daily) && daily.length) return normalizeArchive(daily);

  const arch = await fetchJSON(`/ccu_history/${appId}`);
  if (Array.isArray(arch) && arch.length) return normalizeArchive(arch);

  const live = await fetchJSON(`/live_ccu/${appId}`);
  if (Array.isArray(live) && live.length) return dailyFromLiveSnapshots(live);

  return [];
}

/* ---------------------------- chart helpers -------------------------- */
function toChartRows(game, mode, viewByDaysSinceLaunch) {
  if (!game?.metadata) return [];
  if (mode === "live") {
    if (!Array.isArray(game.live) || game.live.length === 0) return [];
    return game.live.map((r) => ({ x: r.dt, [game.metadata.app_id]: r.ccu }));
  }
  if (!Array.isArray(game.daily) || game.daily.length === 0) return [];
  const release = new Date(game.metadata.release_date);
  const relValid = !Number.isNaN(release.getTime());
  return game.daily.map((r) => {
    const d = new Date(r.dt);
    const key =
      viewByDaysSinceLaunch && relValid
        ? Math.max(0, Math.floor((d - release) / (1000 * 60 * 60 * 24)))
        : fmtDate(d);
    return { x: key, [game.metadata.app_id]: r.ccu };
  });
}

const pct3 = (v) => `${(Number(v) * 100).toFixed(3)}%`;

// Row buffer spacing between stats rows and chart toggles
const ROW_BUFFER_PX = 56; // roughly one game-row height

/* ==================================================================== */
/* UI constants & helpers */
const debounce = (fn, ms = 200) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

const num = (v) =>
  v === null || v === undefined || Number.isNaN(+v)
    ? "—"
    : Number(v).toLocaleString();

// Price formatter (same logic as Steam.jsx)
const formatPrice = (p) => {
  if (p === null || p === undefined || p === "") return null;
  const n = Number(p);
  if (!Number.isFinite(n)) return null;
  if (Math.abs(n) < 1e-6) return "Free to Play";
  return `$ ${n.toLocaleString()}`;
};

// Video embed helper (same as Steam.jsx)
const getVideoEmbed = (raw) => {
  if (!raw) return null;
  const url = raw.trim();
  const lower = url.toLowerCase();

  if (/\.(mp4|webm|ogg)(\?|#|$)/.test(lower)) {
    return { type: "file", src: url };
  }
  const yt = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/
  );
  if (yt) {
    const id = yt[1];
    return {
      type: "youtube",
      src: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&playsinline=1&rel=0&showinfo=0&loop=1&playlist=${id}`,
    };
  }
  return null;
};

/* ---------- social link helpers (no hooks) ---------- */
const normalizeUrl = (u) => {
  let s = String(u || "").trim();
  if (!s) return "";
  if (!/^https?:\/\//i.test(s)) s = "https://" + s.replace(/^\/+/, "");
  return s;
};
const getSocialLinks = (md = {}) => {
  const out = [];
  const add = (prop, label) => {
    const raw = (md?.[prop] ?? "").toString().trim();
    if (!raw) return;
    const href = normalizeUrl(raw);
    if (!href) return;
    out.push({ label, href });
  };
  add("discord", "Discord");
  add("youtube", "YouTube");
  add("facebook", "Facebook");
  add("x", "X");
  add("twitch", "Twitch");
  return out;
};

/* ---------- name normalizers (no hooks) ---------- */
// ---------- name normalizers (no hooks) ----------
const norm = (s = "") =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")  // <-- remove diacritics like in Twitch.jsx
    .replace(/[’'`]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normMS = norm;

/* ==================================================================== */

// === Fancy backdrop loader that uses the game's box art ===
function ChartImageLoader({ src, height = 600 }) {
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height,
        borderRadius: 12,
        overflow: "hidden",
        background: "#0b0b0b",
        border: "1px solid #222",
      }}
    >
      {/* Blown-up, darkened background */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: src ? `url(${src})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: "scale(1.18)",
          filter: "saturate(120%)",
          opacity: 0.10,
          transition: "opacity .2s ease",
        }}
      />
      {/* Soft vignette */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(120% 120% at 50% -20%, rgba(63,169,245,0.12), transparent 40%), radial-gradient(120% 120% at 50% 110%, rgba(0,0,0,0.55), rgba(0,0,0,0.85))",
          mixBlendMode: "screen",
        }}
      />
      {/* Sheen sweep */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(115deg, transparent 0%, rgba(255,255,255,0.08) 40%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0.08) 60%, transparent 100%)",
          transform: "translateX(-120%)",
          animation: "sheen 1.6s ease-in-out infinite",
        }}
      />
      {/* Text hint */}
      <div
        style={{
          position: "absolute",
          left: 16,
          bottom: 14,
          padding: "6px 10px",
          background: "rgba(0,0,0,0.45)",
          border: "1px solid #222",
          borderRadius: 8,
          fontSize: 13,
          color: "#cfd8e3",
          fontWeight: 700,
          letterSpacing: ".2px",
        }}
      >
        Loading mindSHARE…
      </div>

      {/* keyframes (scoped here) */}
      <style>
        {`@keyframes sheen {
            0%   { transform: translateX(-120%); }
            60%  { transform: translateX(120%); }
            100% { transform: translateX(120%); }
          }`}
      </style>
    </div>
  );
}

// A plain backdrop for *loaded* charts (very dark)
function ChartBackdrop({ src, height = 600 }) {
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: src ? `url(${src})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: "scale(1.25)",
          filter: "saturate(120%) brightness(0.02)", // <- extra dark once loaded
          opacity: 1,
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.60), rgba(0,0,0,0.82))",
        }}
      />
    </div>
  );
}

function TwoColSkeleton({ img }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(360px, 460px) 1fr",
        gap: 24,
        alignItems: "stretch",
        padding: "0 0 0 0",
      }}
    >
      {/* Sidebar skeleton (matches your left panel sizes/spacings) */}
      <aside
        style={{
          position: "relative",
          overflow: "hidden",
          background: "#0f0f0f",
          borderRight: "1px solid #222",
          borderRadius: 0,
          boxShadow: "none",
          padding: 20,
          minHeight: "calc(var(--mg-vh) * 100)",
          width: 440,
        }}
      >
        {/* Subtle blown-up background like the real sidebar */}
        {img && (
          <>
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${img})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                transform: "scale(1.25)",
                filter: "saturate(120%)",
                opacity: 0.1,
                zIndex: 0,
              }}
            />
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.60), rgba(0,0,0,0.82))",
                zIndex: 0,
              }}
            />
          </>
        )}

        {/* Foreground shimmering blocks */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            style={{
              height: 220,
              borderRadius: 12,
              background: "linear-gradient(90deg,#111 25%,#1a1a1a 37%,#111 63%)",
              backgroundSize: "400% 100%",
              animation: "mgShimmer 1.4s ease-in-out infinite",
              marginBottom: 14,
              border: "1px solid #222",
            }}
          />
          <div
            style={{
              height: 18,
              width: "70%",
              borderRadius: 6,
              background: "linear-gradient(90deg,#111 25%,#1a1a1a 37%,#111 63%)",
              backgroundSize: "400% 100%",
              animation: "mgShimmer 1.4s ease-in-out infinite",
              margin: "0 0 12px 0",
            }}
          />
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              style={{
                height: 14,
                width: i % 2 ? "88%" : "62%",
                borderRadius: 6,
                background:
                  "linear-gradient(90deg,#111 25%,#1a1a1a 37%,#111 63%)",
                backgroundSize: "400% 100%",
                animation: "mgShimmer 1.4s ease-in-out infinite",
                margin: "10px 0",
              }}
            />
          ))}
        </div>
      </aside>

      {/* Right side: chart placeholder that uses the same blown-up image look */}
      <main style={{ padding: "28px 56px 24px 24px" }}>
        <ChartImageLoader src={img} height={600} />
      </main>

      {/* shimmer keyframes */}
      <style>{`
        @keyframes mgShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}

function GameDetail() {
  // ---- hooks kept in stable order ----
  const { app_id } = useParams();
  const { metadata } = useGlobalData();
  const { tikTokData, metaData: tiktokMetaData, loading: tiktokGlobalLoading } = useTikTokData();

  const [mainGame, setMainGame] = useState(null);
  const [compareGames, setCompareGames] = useState([]);
  const [availableGames, setAvailableGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);

  const [dataMode, setDataMode] = useState("live");
  const [viewByDaysSinceLaunch, setViewByDaysSinceLaunch] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);
  const [loadingCCU, setLoadingCCU] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [summary, setSummary] = useState(null);

  const [winStartIdx, setWinStartIdx] = useState(0);
  const [winEndIdx, setWinEndIdx] = useState(0);
  const hasInitWindow = useRef(false);

  const [chartTab, setChartTab] = useState("mindshare");
  const [showRegionBands, setShowRegionBands] = useState(false);

  const [hoveredRowId, setHoveredRowId] = useState(null);

  // TikTok state
  const [loadingTikTok, setLoadingTikTok] = useState(false);
  const [tiktokSeries, setTiktokSeries] = useState([]); // [{date, views, deltaViews}]
  const [tiktokHashtag, setTiktokHashtag] = useState("");
  const [tiktokViewMode, setTikTokViewMode] = useState("lifetime");
  const [tiktokCompare, setTikTokCompare] = useState([]);

  // mindSHARE state
  const [mindshareViewMode, setMindshareViewMode] = useState("level"); // (kept, not used for axis now)
  const [loadingMindshare, setLoadingMindshare] = useState(false);
  const [mindshareSeries, setMindshareSeries] = useState([]); // [{date, w2l, level, weekly, rank}]
  const [mindshareCompare, setMindshareCompare] = useState([]); // [{ name, series:[...] }]
  const mindshareIndexRef = useRef(null); // Map<normName, [{dateISO, w2l, level, weekly, rank}]>
const [mindshareAgg, setMindshareAgg] = useState("weekly"); // 'weekly' | 'cumulative'

// Twitch rank map: norm(game_name) -> rank (1 = best)
const [twitchRankByName, setTwitchRankByName] = useState(new Map());


  // NEW: axis mode for mindSHARE
  const [mindshareXAxisMode, setMindshareXAxisMode] = useState("w2l"); // 'date' | 'w2l'

  const seriesColor = (i) =>
    ["#3fa9f5", "#ffcc00", "#00cc99", "#ff66cc", "#66ccff"][i % 5];

  const steamById = useMemo(() => {
    const m = new Map();
    (metadata || []).forEach((g) => m.set(String(g.app_id), g));
    return m;
  }, [metadata]);

  // options for TikTok "add game"
  const tiktokGameOptions = useMemo(() => {
    const arr = [];
    if (!Array.isArray(tiktokMetaData)) return arr;
    const seen = new Set();
    for (const m of tiktokMetaData) {
      const name = (m.Game || m.GameName || "").trim();
      if (!name) continue;
      const key = norm(name);
      if (seen.has(key)) continue;
      seen.add(key);
      arr.push({
        label: name,
        value: name,
        _lc: name.toLowerCase(),
        imageUrl: m.ImageURL || "",
        steam_id: m.Steam_ID || m.SteamID || m.steam_id || null,
        publisher: m.Publisher || "",
        release: m.Release_Date || m.ReleaseDate || "",
      });
    }
    return arr.sort((a, b) => a.label.localeCompare(b.label));
  }, [tiktokMetaData]);

  // Past 7 days TikTok views delta per game (norm(name) -> 7d delta)
const tiktok7dMap = useMemo(() => {
  const by = new Map(); // norm(name) -> [{ d: Date, views: number }]
  if (Array.isArray(tikTokData)) {
    for (const r of tikTokData) {
      const key = norm(r.game || "");
      if (!key) continue;
      const d = new Date(r.dt);
      if (Number.isNaN(d)) continue;
      const v = Number(r.views) || 0;
      if (!by.has(key)) by.set(key, []);
      by.get(key).push({ d, views: v });
    }
  }

  const out = new Map();
  for (const [key, arr] of by.entries()) {
    arr.sort((a, b) => a.d - b.d);
    if (!arr.length) continue;

    const latest = arr[arr.length - 1];
    const target = new Date(latest.d);
    target.setHours(0, 0, 0, 0);
    target.setDate(target.getDate() - 7);

    // find closest index <= target
    let idx = arr.length - 2;
    while (idx >= 0 && arr[idx].d > target) idx--;
    if (idx < 0) idx = 0;

    const base = Number(arr[idx]?.views || 0);
    const delta = Number(latest.views || 0) - base;
    out.set(key, delta);
  }
  return out;
}, [tikTokData]);


  useEffect(() => {
    if (!metadata?.length) return;
    const ALLOWED = new Set(["game"]);
    const clean = metadata
      .filter((g) => g.app_id && g.name && ALLOWED.has(String(g.type || "").toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
    setAvailableGames(clean);
  }, [metadata]);

  const gameOptions = useMemo(
    () =>
      availableGames.map((g) => ({
        label: g.name,
        value: String(g.app_id),
        _lc: (g.name || "").toLowerCase(),
        box_art: g.box_art,
        ...g,
      })),
    [availableGames]
  );

  useEffect(() => {
    if (!metadata?.length) return;
    const md = metadata.find((g) => String(g.app_id) === String(app_id));
    if (md) {
      setMainGame({ metadata: md, daily: [], live: [], reviews: [] });
    } else {
      setMainGame({
        metadata: {
          name: "Unknown Game",
          box_art: "",
          short_description: "",
          app_id,
          release_date: new Date().toISOString(),
        },
        daily: [],
        live: [],
        reviews: [],
      });
    }
  }, [app_id, metadata]);

  useEffect(() => {
    if (!mainGame?.metadata || mainGame.metadata.name === "Unknown Game") return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingCCU(true);
        setLoadingReviews(true);
        const [live, daily, reviews, summaryResp] = await Promise.all([
          loadLiveSeries(mainGame.metadata.app_id),
          loadDailySeries(mainGame.metadata.app_id),
          fetchJSON(`/reviews/${mainGame.metadata.app_id}`).then((r) => (Array.isArray(r) ? r : [])),
          fetchJSON(`/summary/${mainGame.metadata.app_id}`),
        ]);
        if (!cancelled) {
          setMainGame((prev) => ({ ...prev, live, daily, reviews }));
          setSummary(summaryResp);
        }
      } catch (e) {
        console.error("❌ main data load failed:", e);
      } finally {
        if (!cancelled) {
          setLoadingCCU(false);
          setLoadingReviews(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mainGame?.metadata?.app_id]);

  /* ====================== FIX: robust mindSHARE index build ====================== */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (mindshareIndexRef.current) return; // already built
      try {
        setLoadingMindshare(true);
        const text = await fetchGzipText(GAME_STATS_URL);
        // Use d3.csvParse to handle quoted commas safely
        const rows = d3.csvParse(text); 

        // Build index: norm(Game) -> [{dateISO, w2l, level, weekly, rank}]
        const idx = new Map();

        for (const r of rows) {
  const game = (r.Game ?? r.game ?? "").trim();

  const periodRaw =
    r.Period ??
    r["Period (DATE)"] ??
    r.period ??
    r.Date ??
    r.date ??
    "";

  const msRaw =
    r.mindSHARE ??
    r.MindSHARE ??
    r.mindshare ??
    r.Mindshare ??
    r["mindSHARE"] ??
    r["Mind Share"];

  const rankRaw =
    r.Rank___mindSHARE ??
    r["Rank___mindSHARE"] ??
    r.Rank__mindSHARE ??
    r["Rank__mindSHARE"] ??
    r.Rank_mindSHARE ??
    r.rank_mindSHARE ??
    r.rank ??
    null;

  const searchRankRaw =
    r.Rank___Search ??
    r["Rank___Search"] ??
    r.Rank__Search ??
    r["Rank__Search"] ??
    r.Rank_Search ??
    r.rank_search ??
    null;

  const videoRankRaw =
    r.Rank___Video ??
    r["Rank___Video"] ??
    r.Rank__Video ??
    r["Rank__Video"] ??
    r.Rank_Video ??
    r["rank_video"] ??
    r.video_rank ??
    null;

  const w2lRaw =
    r.Weeks_to_Launch ??
    r["Weeks_to_Launch"] ??
    r["Weeks to Launch"] ??
    r.weeks_to_launch ??
    r.w2l ??
    r.W2L ??
    "";

  const ms = Number(msRaw);
  if (!game || !Number.isFinite(ms)) continue;

  const d = new Date(periodRaw);
  if (Number.isNaN(d)) continue;
  const dateISO = d.toISOString().slice(0, 10);

  // keep row even if W2L is blank (post-launch)
  const nW2L = parseInt(w2lRaw, 10);
  const w2l = Number.isFinite(nW2L) ? nW2L : null;

  const key = normMS(game);
  if (!idx.has(key)) idx.set(key, []);
  idx.get(key).push({
    dateISO,
    w2l,
    level: ms,
    rank:       rankRaw == null || rankRaw === "" ? null : parseInt(rankRaw, 10),
    searchRank: searchRankRaw == null || searchRankRaw === "" ? null : parseInt(searchRankRaw, 10),
    videoRank:  videoRankRaw == null || videoRankRaw === "" ? null : parseInt(videoRankRaw, 10),
  });
}


        // sort + weekly delta (kept for compatibility)
        for (const arr of idx.values()) {
          arr.sort((a, b) => (a.dateISO < b.dateISO ? -1 : 1));
          for (let i = 0; i < arr.length; i++) {
            const prev = i > 0 ? arr[i - 1].level : null;
            arr[i].weekly = prev == null ? 0 : arr[i].level - prev;
          }
        }

        if (!cancelled) mindshareIndexRef.current = idx;
      } catch (e) {
        console.error("❌ mindSHARE index build failed:", e);
      } finally {
        if (!cancelled) setLoadingMindshare(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  /* ============================================================================ */

  function buildTikTokSeriesByName(gameName) {
    if (!gameName || !Array.isArray(tikTokData)) return [];
    const target = norm(gameName);
    let rows = tikTokData.filter((r) => norm(r.game || "") === target);
    if (!rows.length) {
      rows = tikTokData.filter((r) => {
        const g = norm(r.game || "");
        return g === target || g.includes(target) || target.includes(g);
      });
    }
    if (!rows.length) return [];
    const mapped = rows
      .map((r) => ({
        date: new Date(r.dt),
        views: Number(r.views) || 0,
        videos: Number(r.videos) || 0,
        hashtag: (r.hashtag || "").trim(),
      }))
      .filter((r) => !isNaN(r.date.getTime()))
      .sort((a, b) => a.date - b.date);

    const byDay = new Map();
    for (const row of mapped) {
      const iso = row.date.toISOString().slice(0, 10);
      const prev = byDay.get(iso);
      if (!prev || row.date > prev._d) {
        byDay.set(iso, { dateISO: iso, views: row.views, videos: row.videos, _d: row.date });
      }
    }
    return Array.from(byDay.values())
      .sort((a, b) => (a.dateISO < b.dateISO ? -1 : 1))
      .map((r, i, arr) => {
        const prev = i > 0 ? arr[i - 1] : null;
        const delta = prev ? r.views - prev.views : 0;
        return { date: r.dateISO, views: r.views, deltaViews: delta };
      });
  }

  function buildMindshareSeriesByName(gameName) {
    const idx = mindshareIndexRef.current;
    if (!idx || !gameName) return [];
    const hit = idx.get(normMS(gameName));
    if (!hit || !hit.length) return [];
    return hit.map((r) => ({
      date: r.dateISO,
      w2l: r.w2l,
      level: r.level,
      weekly: r.weekly,
      rank: r.rank ?? null,
    }));
  }

// Latest Search Volume Rank for a display name (from mindshare index)
function getLatestSearchRank(gameName) {
  if (!gameName) return null;
  const idx = mindshareIndexRef.current;
  const arr = idx ? idx.get(normMS(gameName)) : null;
  if (!arr || !arr.length) return null;
  const last = arr[arr.length - 1];
  return Number.isFinite(last?.searchRank) ? last.searchRank : null;
}
// Latest YouTube (Video) Rank for a display name (from mindSHARE index)
function getLatestVideoRank(gameName) {
  if (!gameName) return null;
  const idx = mindshareIndexRef.current;
  const arr = idx ? idx.get(normMS(gameName)) : null;
  if (!arr || !arr.length) return null;
  const last = arr[arr.length - 1];
  return Number.isFinite(last?.videoRank) ? last.videoRank : null;
}

  // load mindSHARE for main game
  useEffect(() => {
    if (loadingMindshare) return;
    if (!mainGame?.metadata?.name) return;
    const series = buildMindshareSeriesByName(mainGame.metadata.name);
    setMindshareSeries(series);
  }, [loadingMindshare, mainGame?.metadata?.name]);

useEffect(() => {
  if (mindshareXAxisMode !== "w2l") return;
  if (!Array.isArray(mindshareSeries) || mindshareSeries.length === 0) return;
  if (!mindshareSeries.some(r => Number.isFinite(r.w2l))) {
    setMindshareXAxisMode("date");
  }
}, [mindshareSeries, mindshareXAxisMode]);

  // TikTok build for main game
  useEffect(() => {
    const build = async () => {
      if (!mainGame?.metadata?.name) return;
      if (!Array.isArray(tikTokData)) return;
      setLoadingTikTok(true);
      try {
        const series = buildTikTokSeriesByName(mainGame.metadata.name);
        if (!series.length) {
          setTiktokSeries([]);
          setTiktokHashtag("");
          return;
        }
        setTiktokSeries(series);
        setTiktokHashtag("");
      } catch (e) {
        console.error("❌ TikTok build failed:", e);
        setTiktokSeries([]);
        setTiktokHashtag("");
      } finally {
        setLoadingTikTok(false);
      }
    };
    build();
  }, [mainGame?.metadata?.name, tikTokData]);

// Load Twitch daily rollup and compute rank for the most recent day
useEffect(() => {
  let cancelled = false;
  (async () => {
    try {
      const text = await fetchGzipCsvText(TWITCH_DAILY_URL);
      const rows = parseCsvObjects(text); // columns: game_id, game_name, peak_view, viewtime_minutes
      if (!rows?.length) return;

      // 1) Try to discover a date column, if any
      let latestISO = null;
      for (const r of rows) {
        const raw =
          (r.dt || r.date || r.day || "").slice
            ? (r.dt || r.date || r.day || "").slice(0, 10)
            : "";
        if (!raw) continue;
        if (!latestISO || raw > latestISO) latestISO = raw;
      }

      // 2) Aggregate minutes per game
      const minutesByGame = new Map(); // norm(name) -> total minutes
      const nameByKey = new Map();     // for debugging / display if you ever want it

      for (const r of rows) {
        const rowISO =
          (r.dt || r.date || r.day || "").slice
            ? (r.dt || r.date || r.day || "").slice(0, 10)
            : "";

        // If we *have* a date column, only keep the latest day.
        // If we don't, treat the whole file as one snapshot.
        if (latestISO && rowISO && rowISO !== latestISO) continue;

        const name = (r.game_name || r.game || r.Game || r.name || "").trim();
        if (!name) continue;

        const k = norm(name);
        const mins =
          Number(
            r.viewtime_minutes ??
            r.view_minutes ??
            r.minutes ??
            r.viewtime ??
            0
          ) || 0;

        minutesByGame.set(k, (minutesByGame.get(k) || 0) + mins);
        if (!nameByKey.has(k)) nameByKey.set(k, name);
      }

      if (!minutesByGame.size) return;

      // 3) Rank by minutes desc (1 = highest)
      const entries = Array.from(minutesByGame.entries());
      entries.sort((a, b) => b[1] - a[1]);

      const rankMap = new Map();
      let rank = 1;
      for (const [k] of entries) {
        rankMap.set(k, rank++);
      }

      if (!cancelled) setTwitchRankByName(rankMap);
    } catch (e) {
      console.warn("Twitch daily rank load failed:", e);
    }
  })();
  return () => {
    cancelled = true;
  };
}, []);

  // ---- stats rows ----
  // Latest mindSHARE per game (by display name)
  const mindshareLatestByName = useMemo(() => {
    const map = new Map();

    // main game
    if (mainGame?.metadata?.name && mindshareSeries?.length) {
      const last = mindshareSeries[mindshareSeries.length - 1];
      map.set(mainGame.metadata.name, { level: last.level, rank: last.rank ?? null });
    }

    // compared games (series lives in mindshareCompare)
    for (const g of mindshareCompare || []) {
      if (g?.series?.length) {
        const last = g.series[g.series.length - 1];
        map.set(g.name, { level: last.level, rank: last.rank ?? null });
      }
    }
    return map;
  }, [mainGame?.metadata?.name, mindshareSeries, mindshareCompare]);

  const statsRows = useMemo(() => {
        const buildOne = (g, isMain) => {
      const live = g?.live || [];
      const daily = g?.daily || [];

      const latestCCU = live.length ? Number(live[live.length - 1].ccu) : null;

      let peak7 = null;
      if (live.length) peak7 = Math.max(...live.map((d) => Number(d.ccu) || 0));
      if (!live.length && daily.length) {
        peak7 = Math.max(...daily.map((d) => Number(d.ccu) || 0));
      }

      let allTimePeak = null;
      if (daily.length) {
        allTimePeak = Math.max(...daily.map((d) => Number(d.ccu) || 0));
      } else if (live.length) {
        allTimePeak = Math.max(...live.map((d) => Number(d.ccu) || 0));
      }

      const latestReview = (g.reviews || []).slice(-1)[0];
      const posPct =
        latestReview && latestReview.total > 0
          ? (100 * latestReview.positive) / latestReview.total
          : null;

      const k = norm(g?.metadata?.name || "");
      const tikViews = tiktok7dMap.get(k) ?? null;

      // ---- 1) Look up mindGAME (mindgamedataapi) metadata row ----
      let mindMeta: any = null;
      if (Array.isArray(tiktokMetaData)) {
        mindMeta =
          tiktokMetaData.find((m) =>
            norm(m.Game || m.GameName || m.game || "") === k
          ) || null;
      }

      // Image: Steam first, then mindGAME ImageURL
      let img = g?.metadata?.box_art || "";
      if (!img && mindMeta?.ImageURL) {
        img = mindMeta.ImageURL;
      }

      // Base metadata (Steam or stub), then enrich from mindGAME
      const meta = g?.metadata || {};

      const release_date =
        meta.release_date ||
        meta.Release_Date ||
        mindMeta?.["Release Date"] ||
        mindMeta?.Release_Date ||
        "";

      const full_price =
        meta.full_price ??
        meta.Price ??
        mindMeta?.["Pricing/Monetization"] ??
        "";

      const short_description =
        meta.short_description ||
        meta.description ||
        mindMeta?.Description ||
        "";

      const publishers =
        (Array.isArray(meta.publishers)
          ? meta.publishers.join(", ")
          : meta.publishers || meta.publisher) ||
        mindMeta?.Publisher ||
        "";

      const developers =
        (Array.isArray(meta.developers)
          ? meta.developers.join(", ")
          : meta.developers || meta.developer) ||
        mindMeta?.Developer ||
        "";

      const tagsArr = (() => {
        const steamTags = Array.isArray(meta.tags)
          ? meta.tags
          : (meta.tags || "")
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean);

        const mgdTags = (mindMeta?.Tags || "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);

        if (!mgdTags.length) return steamTags;
        const merged = new Set([...steamTags, ...mgdTags]);
        return Array.from(merged);
      })();

      // Video: Steam trailer first, then IGDB Video from mindGAME
      let videoUrl =
        (meta.video || meta.trailer || meta.video_url || "").trim();
      if (!videoUrl && mindMeta) {
        videoUrl =
          (
            mindMeta["IGDB Video"] ||
            mindMeta.IGDB_Video ||
            mindMeta.IGDBVideo ||
            ""
          ).trim();
      }

      const s = isMain ? (summary || {}) : g?.summary || {};
      const estDAU = s?.estimated_dau ?? null;
      const followers = s?.followers ?? null;

      const latestMs =
        mindshareLatestByName.get(g?.metadata?.name || "") ?? null;

      const kName = norm(g?.metadata?.name || "");
      const twitchRank = twitchRankByName.get(kName) ?? null;
      const search_rank = getLatestSearchRank(g?.metadata?.name || "");
      const video_rank = getLatestVideoRank(g?.metadata?.name || "");

      return {
        id: String(g?.metadata?.app_id || ""),
        key: String(
          g?.metadata?.app_id || g?.metadata?.name || Math.random()
        ),
        name: g?.metadata?.name || "Unknown",
        img,
        mindshare: latestMs ? latestMs.level : null,
        mindshare_rank: latestMs ? latestMs.rank : null,
        current_ccu: latestCCU,
        peak_7d: peak7,
        all_time_peak: allTimePeak,
        positive_pct: posPct,
        estimated_dau: estDAU,
        followers: followers,
        search_rank,
        twitch_rank: twitchRank,
        video_rank,
        tiktok_views: tikViews,

        // metadata for hover popout (Steam + mindGAME)
        release_date,
        full_price,
        short_description,
        publishers,
        developers,
        tags: tagsArr,
        videoUrl,
      };
    };


    const rows = [];
    if (mainGame) rows.push(buildOne(mainGame, true));
    for (const g of compareGames.slice(0, 4)) rows.push(buildOne(g, false));
    return rows;
  }, [mainGame, compareGames, tiktok7dMap, tiktokMetaData, summary, mindshareLatestByName, twitchRankByName]);

  const columnMax = useMemo(() => {
    const keys = [
      "mindshare",
      "current_ccu",
      "peak_7d",
      "all_time_peak",
      "positive_pct",
      "estimated_dau",
      "followers",
      "tiktok_views",
    ];
    const out = {};
    for (const k of keys) {
      const vals = (statsRows || [])
        .map((r) => r[k])
        .filter((v) => v != null && Number.isFinite(Number(v)));
      out[k] = vals.length ? Math.max(...vals.map(Number)) : null;
    }
    return out;
  }, [statsRows]);

// Best (lowest) Twitch rank across the current rows
const twitchBestRank = useMemo(() => {
  const vals = (statsRows || [])
    .map((r) => r.twitch_rank)
    .filter((v) => v != null && Number.isFinite(Number(v)));
  if (!vals.length) return null;
  return Math.min(...vals.map(Number));
}, [statsRows]);

// Best (lowest) Search rank across the current rows
const searchBestRank = useMemo(() => {
  const vals = (statsRows || [])
    .map((r) => r.search_rank)
    .filter((v) => v != null && Number.isFinite(Number(v)));
  if (!vals.length) return null;
  return Math.min(...vals.map(Number));
}, [statsRows]);

// Best (lowest) YouTube (Video) rank across the current rows
const youtubeBestRank = useMemo(() => {
  const vals = (statsRows || [])
    .map(r => r.video_rank)
    .filter(v => v != null && Number.isFinite(Number(v)));
  if (!vals.length) return null;
  return Math.min(...vals.map(Number));
}, [statsRows]);

  const topCellStyle = (isTop) => ({
    fontWeight: 800,
    background: isTop ? "#1b1b1b" : "transparent",
    borderRadius: 6,
    padding: "4px 6px",
  });

// NEW: header cell + icon styles
const headerCellStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  color: "#a8adb3",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const headerIconStyle = {
  width: 16,
  height: 16,
  objectFit: "contain",
  display: "block",
};

  // merged CCU chart data for players tab
  const mergedData = useMemo(() => {
    const bucket = {};
    const src = [mainGame, ...compareGames].filter(Boolean);

    const _xToNumber = (x) => {
      if (dataMode === "live") return new Date(x).getTime();
      if (viewByDaysSinceLaunch) return Number(x);
      return new Date(x).getTime();
    };

    const lastXPerId = new Map();

    src.forEach((g) => {
      const rows = toChartRows(g, dataMode, viewByDaysSinceLaunch);
      let maxX = -Infinity;

      rows.forEach((pt) => {
        const id = g.metadata.app_id;
        const xKey = pt.x;
        const v = pt[id];

        if (!bucket[xKey]) bucket[xKey] = { x: xKey };

        const prev = bucket[xKey][id];
        const nv = prev == null ? v : Math.max(Number(prev) || 0, Number(v) || 0);
        bucket[xKey][id] = nv;

        const xn = _xToNumber(xKey);
        if (Number.isFinite(xn) && xn > maxX) maxX = xn;
      });

      lastXPerId.set(String(g.metadata.app_id), maxX);
    });

    const arr = Object.values(bucket);
    if (dataMode === "live") {
      arr.sort((a, b) => new Date(a.x) - new Date(b.x));
    } else {
      arr.sort((a, b) =>
        viewByDaysSinceLaunch
          ? parseInt(a.x, 10) - parseInt(b.x, 10)
          : new Date(a.x) - new Date(b.x)
      );
    }

    const ids = src.map((g) => String(g.metadata.app_id));
    const lastVal = Object.create(null);

    for (const row of arr) {
      const rowXn =
        dataMode === "live" || !viewByDaysSinceLaunch
          ? new Date(row.x).getTime()
          : Number(row.x);
      for (const id of ids) {
        const v = row[id];
        if (v == null || !Number.isFinite(Number(v))) {
          const cutoff = lastXPerId.get(id);
          if (lastVal[id] != null && cutoff != null && rowXn <= cutoff) {
            row[id] = lastVal[id];
          }
        } else {
          lastVal[id] = Number(v);
        }
      }
    }

    return arr;
  }, [mainGame, compareGames, dataMode, viewByDaysSinceLaunch]);

  // windowed renderData
  const renderData = useMemo(() => {
    if (!mergedData || mergedData.length === 0) return [];
    const s = Math.max(0, Math.min(winStartIdx, mergedData.length - 1));
    const e = Math.max(s, Math.min(winEndIdx, mergedData.length - 1));
    return mergedData.slice(s, e + 1);
  }, [mergedData, winStartIdx, winEndIdx]);

  useEffect(() => {
    if (!mergedData || mergedData.length === 0) return;
    if (!hasInitWindow.current) {
      setWinStartIdx(0);
      setWinEndIdx(mergedData.length - 1);
      hasInitWindow.current = true;
    } else {
      setWinStartIdx((prev) =>
        Math.max(0, Math.min(prev, Math.max(0, mergedData.length - 2)))
      );
      setWinEndIdx((prev) =>
        Math.max(1, Math.min(prev, Math.max(1, mergedData.length - 1)))
      );
    }
  }, [mergedData.length]);

  useEffect(() => {
    if (mergedData && mergedData.length > 0) {
      setWinStartIdx(0);
      setWinEndIdx(mergedData.length - 1);
    }
  }, [dataMode, mergedData.length]);

  const appNames = useMemo(() => {
    const map = {};
    [mainGame, ...compareGames].forEach((g) => {
      if (g?.metadata?.app_id) map[g.metadata.app_id] = g.metadata.name;
    });
    return map;
  }, [mainGame, compareGames]);

  const currentMainSeries =
    dataMode === "live" ? (mainGame?.live || []) : (mainGame?.daily || []);

  // TikTok combined chart data
  const tiktokCombinedData = useMemo(() => {
    const seriesMap = new Map();
    if (tiktokSeries.length && mainGame?.metadata?.name) {
      seriesMap.set(mainGame.metadata.name, tiktokSeries);
    }
    for (const g of tiktokCompare) {
      if (g.series?.length) seriesMap.set(g.name, g.series);
    }
    const dates = Array.from(
      new Set(
        Array.from(seriesMap.values()).flatMap((arr) => arr.map((r) => r.date))
      )
    ).sort((a, b) => new Date(a) - new Date(b));

    return dates.map((d) => {
      const row = { date: d };
      for (const [name, arr] of seriesMap.entries()) {
        const hit = arr.find((r) => r.date === d);
        row[name] = hit
          ? tiktokViewMode === "lifetime"
            ? hit.views
            : hit.deltaViews
          : undefined;
      }
      return row;
    });
  }, [tiktokSeries, tiktokCompare, tiktokViewMode, mainGame?.metadata?.name]);

  const tiktokMaxVal = useMemo(() => {
    let max = 0;
    for (const row of tiktokCombinedData) {
      for (const [k, v] of Object.entries(row)) {
        if (k === "date") continue;
        if (typeof v === "number" && v > max) max = v;
      }
    }
    return max;
  }, [tiktokCombinedData]);

  const tiktokYAxisWidth = useMemo(() => {
    const label = (tiktokMaxVal || 0).toLocaleString();
    return Math.min(220, Math.max(66, 18 + label.length * 9));
  }, [tiktokMaxVal]);

  // mindSHARE combined chart data (Date or Weeks-to-Launch on X, level on Y)
  const mindshareCombinedData = useMemo(() => {
    const series = new Map();
    if (mindshareSeries.length && mainGame?.metadata?.name) {
      series.set(mainGame.metadata.name, mindshareSeries);
    }
    for (const g of mindshareCompare) {
      if (g.series?.length) series.set(g.name, g.series);
    }

    const keySet = new Set();
  if (mindshareXAxisMode === "w2l") {
    for (const arr of series.values()) for (const r of arr) if (Number.isFinite(r.w2l)) keySet.add(r.w2l);
  } else {
      for (const arr of series.values()) for (const r of arr) keySet.add(r.date);
    }

    const keys = Array.from(keySet.values()).sort((a, b) =>
      mindshareXAxisMode === "w2l" ? Number(a) - Number(b) : a < b ? -1 : 1
    );

    const rows = keys.map((key) => {
      const row = { x: key };
      for (const [name, arr] of series.entries()) {
              const hit = arr.find((r) =>
        mindshareXAxisMode === "w2l"
          ? Number.isFinite(r.w2l) && r.w2l === key
          : r.date === key
      );
        if (hit) row[name] = hit.level; // always level on Y
      }
      return row;
    });

    return rows;
  }, [mindshareSeries, mindshareCompare, mindshareXAxisMode, mainGame?.metadata?.name]);

  // --- mindSHARE window state (like Players) ---
  const [mindWinStartIdx, setMindWinStartIdx] = useState(0);
  const [mindWinEndIdx, setMindWinEndIdx] = useState(0);
  const mindHasInitRef = useRef(false);

  // init/clamp window whenever the series changes or axis toggles
  useEffect(() => {
    const len = mindshareCombinedData.length;
    if (!len) return;
    if (!mindHasInitRef.current) {
      setMindWinStartIdx(0);
      setMindWinEndIdx(len - 1);
      mindHasInitRef.current = true;
    } else {
      setMindWinStartIdx((prev) => Math.max(0, Math.min(prev, Math.max(0, len - 2))));
      setMindWinEndIdx((prev) => Math.max(1, Math.min(prev, Math.max(1, len - 1))));
    }
  }, [mindshareCombinedData.length, mindshareXAxisMode]);

  // slice chart data to window
  const mindshareRenderData = useMemo(() => {
    const len = mindshareCombinedData.length;
    if (!len) return [];
    const s = Math.max(0, Math.min(mindWinStartIdx, len - 1));
    const e = Math.max(s, Math.min(mindWinEndIdx, len - 1));
    return mindshareCombinedData.slice(s, e + 1);
  }, [mindshareCombinedData, mindWinStartIdx, mindWinEndIdx]);

// Max of the currently plotted WEEKLY window (raw level values, non-negative)
const msMaxWeeklyWindow = useMemo(() => {
  let hi = 0;
  for (const row of mindshareRenderData) {
    for (const [k, v] of Object.entries(row)) {
      if (k === "x") continue;
      const n = Number(v);
      if (Number.isFinite(n) && n > hi) hi = n;
    }
  }
  return hi;
}, [mindshareRenderData]);

// If W2L+Cumulative, build a running-sum version of the CURRENT window; otherwise use original window.
const mindshareChartData = useMemo(() => {
  if (mindshareXAxisMode !== "w2l" || mindshareAgg !== "cumulative") return mindshareRenderData;
  const running = Object.create(null);
  return mindshareRenderData.map((row) => {
    const out = { x: row.x };
    for (const [k, v] of Object.entries(row)) {
      if (k === "x") continue;
      const n = Number(v);
      if (!Number.isFinite(n)) continue;
      running[k] = (running[k] ?? 0) + n;
      out[k] = running[k];
    }
    return out;
  });
}, [mindshareRenderData, mindshareXAxisMode, mindshareAgg]);

// Max for the chart we’re actually plotting (weekly uses original max; cumulative uses running-sum max)
const msMaxCum = useMemo(() => {
  if (!(mindshareXAxisMode === "w2l" && mindshareAgg === "cumulative")) return 0;
  let hi = 0;
  for (const row of mindshareChartData) {
    for (const [k, v] of Object.entries(row)) {
      if (k === "x") continue;
      const n = Number(v);
      if (Number.isFinite(n) && n > hi) hi = n;
    }
  }
  return hi;
}, [mindshareChartData, mindshareAgg, mindshareXAxisMode]);

// Dynamic Y label width so large cumulative numbers don’t clip
const mindshareYAxisWidthDyn = useMemo(() => {
  const hi = (mindshareXAxisMode === "w2l" && mindshareAgg === "cumulative")
    ? msMaxCum
    : msMaxWeeklyWindow; // windowed weekly max
  const label = (hi || 0).toLocaleString();
  return Math.min(220, Math.max(66, 18 + label.length * 9));
}, [mindshareXAxisMode, mindshareAgg, msMaxCum, msMaxWeeklyWindow]);

// ---- cumulative wiring (must be after mindshareRenderData/mindshareChartData) ----
const isCum = mindshareXAxisMode === "w2l" && mindshareAgg === "cumulative";
const plotted = isCum ? mindshareChartData : mindshareRenderData;
const chartMargin = useMemo(
  () => ({ left: 10, right: isCum ? 60 : 20, top: 0, bottom: 0 }),
  [isCum]
);

  // pretty label for slider HUD
  const formatW2L = (w) => (Number.isFinite(+w) ? String(parseInt(w, 10)) : String(w));
  const labelForMindIdx = (idx) => {
    const len = mindshareCombinedData.length;
    if (!len) return "";
    const x = mindshareCombinedData[Math.min(Math.max(idx, 0), len - 1)].x;
    if (mindshareXAxisMode === "w2l") return formatW2L(x); 
    const d = new Date(x);
    return isNaN(d) ? String(x) : d.toLocaleDateString();
  };

  const mindshareMaxVal = useMemo(() => {
    let max = 0;
    for (const row of mindshareCombinedData) {
      for (const [k, v] of Object.entries(row)) {
        if (k === "x") continue;
        if (typeof v === "number" && isFinite(v) && Math.abs(v) > Math.abs(max))
          max = v;
      }
    }
    return max;
  }, [mindshareCombinedData]);

  const mindshareYAxisWidth = useMemo(() => {
    const label = (mindshareMaxVal || 0).toLocaleString();
    return Math.min(220, Math.max(66, 18 + label.length * 9));
  }, [mindshareMaxVal]);

  // -------- actions --------

  // Remove a compared game everywhere (CCU charts, TikTok, mindSHARE)
  const removeComparedGame = ({ id, name }) => {
    // Drop from compareGames (drives CCU & reviews compare)
    setCompareGames(prev =>
      prev.filter(g => String(g?.metadata?.app_id) !== String(id))
    );

    // Drop from TikTok compare series (keyed by display name)
    setTikTokCompare(prev =>
      prev.filter(x => x.name !== name)
    );

    // Drop from mindSHARE compare series (keyed by display name)
    setMindshareCompare(prev =>
      prev.filter(x => x.name !== name)
    );
  };

  const addGameFromOption = async (opt) => {
    if (!opt) return;
    const selId = String(opt.app_id ?? opt.value);
    if (String(selId) === String(app_id)) return;
    if (compareGames.find((g) => String(g?.metadata?.app_id) === selId)) return;

    try {
      const [live, daily, reviews, summary] = await Promise.all([
        loadLiveSeries(selId),
        loadDailySeries(selId),
        fetchJSON(`/reviews/${selId}`).then((r) => (Array.isArray(r) ? r : [])),
        fetchJSON(`/summary/${selId}`),
      ]);

      const steamMeta = steamById.get(selId);
      const md =
        steamMeta ||
        availableGames.find((g) => String(g.app_id) === selId) || {
          name: opt.label || `App ${selId}`,
          app_id: selId,
          box_art: opt.box_art,
        };

      setCompareGames((prev) => [
        ...prev,
        { metadata: md, live, daily, reviews, summary },
      ]);

      const tkSeries = buildTikTokSeriesByName(md.name);
      if (tkSeries.length) {
        setTikTokCompare((prev) => {
          if (prev.find((x) => x.name === md.name)) return prev;
          return [...prev, { name: md.name, series: tkSeries }];
        });
      }

      const msSeries = buildMindshareSeriesByName(md.name);
      if (msSeries.length) {
        setMindshareCompare((prev) => {
          if (prev.find((x) => x.name === md.name)) return prev;
          return [...prev, { name: md.name, series: msSeries }];
        });
      }
    } catch (e) {
      console.error("❌ compare load failed:", e);
    }
  };

  const addTikTokGame = async (opt) => {
    if (!opt) return;

    const label = opt.label || opt.value || "";
    const normName = norm(label);

    const usedNames = new Set([
      norm(mainGame?.metadata?.name || ""),
      ...compareGames.map((g) => norm(g?.metadata?.name || "")),
    ]);
    if (usedNames.has(normName)) return;

    const tMeta =
      (tiktokMetaData || []).find((x) => norm(x.Game || x.GameName || "") === normName) || null;

    const steamId = String(tMeta?.Steam_ID || opt.steam_id || "");
    if (steamId && steamById.get(steamId)) {
      await addGameFromOption({
        app_id: steamId,
        label: steamById.get(steamId)?.name || label,
        box_art: steamById.get(steamId)?.box_art || tMeta?.ImageURL || opt.imageUrl,
      });
      return;
    }

    const md = {
      name: label,
      app_id: `tiktok:${label}`,
      box_art: tMeta?.ImageURL || opt.imageUrl || "",
      release_date: tMeta?.Release_Date || opt.release || "",
      publishers: tMeta?.Publisher ? [tMeta.Publisher] : [],
    };
    setCompareGames((prev) => [
      ...prev,
      { metadata: md, live: [], daily: [], reviews: [], summary: null },
    ]);

    const tkSeries = buildTikTokSeriesByName(label);
    if (tkSeries.length) {
      setTikTokCompare((prev) => {
        if (prev.find((x) => x.name === label)) return prev;
        return [...prev, { name: label, series: tkSeries }];
      });
    }

    const msSeries = buildMindshareSeriesByName(label);
    if (msSeries.length) {
      setMindshareCompare((prev) => {
        if (prev.find((x) => x.name === label)) return prev;
        return [...prev, { name: label, series: msSeries }];
      });
    }
  };

// Add a game to the mindSHARE compare set using the same behavior as TikTok
const addMindshareGame = async (opt) => {
  if (!opt) return;

  const label = opt.label || opt.value || "";
  const normName = norm(label);

  // Avoid duplicates (main + compareGames + mindshareCompare)
  const usedNames = new Set([
    norm(mainGame?.metadata?.name || ""),
    ...compareGames.map((g) => norm(g?.metadata?.name || "")),
    ...mindshareCompare.map((g) => norm(g?.name || "")),
  ]);
  if (usedNames.has(normName)) return;

  // Try to map to a Steam app via TikTok metadata first
  const tMeta =
    (tiktokMetaData || []).find(
      (x) => norm(x.Game || x.GameName || "") === normName
    ) || null;

  const steamId = String(tMeta?.Steam_ID || opt.steam_id || "");

  // If Steam ID maps to known Steam metadata, hydrate via the normal compare path
  if (steamId && steamById.get(steamId)) {
    try {
      await addGameFromOption({
        app_id: steamId,
        label: steamById.get(steamId)?.name || label,
        box_art:
          steamById.get(steamId)?.box_art || tMeta?.ImageURL || opt.imageUrl,
      });
    } catch (e) {
      console.warn("addMindshareGame: addGameFromOption failed; continuing with MS-only.", e);
    }
  } else {
    // ❗ No Steam mapping — behave like TikTok: push a lightweight entry into compareGames
    const msMeta = {
      name: label,
      app_id: `ms:${label}`, // distinct namespace (TikTok uses `tiktok:`)
      box_art: tMeta?.ImageURL || opt.imageUrl || "",
      release_date: tMeta?.Release_Date || opt.release || "",
      publishers: tMeta?.Publisher ? [tMeta.Publisher] : [],
    };

    const alreadyInCompare =
      compareGames.some(
        (g) =>
          norm(g?.metadata?.name || "") === normName ||
          String(g?.metadata?.app_id || "") === msMeta.app_id
      );

    if (!alreadyInCompare) {
      setCompareGames((prev) => [
        ...prev,
        { metadata: msMeta, live: [], daily: [], reviews: [], summary: null },
      ]);
    }
  }

  // Always add the mindSHARE series so it plots on the MS chart
  const msSeries = buildMindshareSeriesByName(label);
  if (msSeries.length) {
    setMindshareCompare((prev) => {
      if (prev.find((x) => norm(x.name) === normName)) return prev;
      return [...prev, { name: label, series: msSeries }];
    });
  } else {
    console.info("No mindSHARE series found for:", label);
  }
};


  const labelForIdx = (idx) => {
    if (!mergedData || mergedData.length === 0) return "";
    const x = mergedData[Math.min(Math.max(idx, 0), mergedData.length - 1)].x;
    return dataMode === "live" ? fmtDateTime(x) : String(x);
  };

  const pub = Array.isArray(mainGame?.metadata?.publishers)
    ? mainGame.metadata.publishers.join(", ")
    : mainGame?.metadata?.publishers;
  const dev = Array.isArray(mainGame?.metadata?.developers)
    ? mainGame.metadata.developers.join(", ")
    : mainGame?.metadata?.developers;

  const ghostBtn = {
    height: 36,
    width: 116,
    padding: 0,
    background: "rgba(63,169,245,0.12)",
    color: "#3fa9f5",
    border: "1px solid #3fa9f5",
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    backdropFilter: "blur(2px)",
    lineHeight: 1,
  };
  const ghostLink = { ...ghostBtn, textDecoration: "none" };

  const socialLinks = getSocialLinks(mainGame?.metadata || {});

  /* --------------------------------- RENDER --------------------------------- */
  return (
    <div style={{ background: "#000", color: "#fff", minHeight: "calc(var(--mg-vh) * 100)" }}>
      {!mainGame?.metadata ? (
        <TwoColSkeleton img={/* optional */ undefined} />
      ) : (
        <>
          {/* ==== 2-column layout (flush sidebar) ==== */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(360px, 460px) 1fr",
              gap: 24,
              alignItems: "stretch",
            }}
          >
            {/* Sidebar */}
            <aside
              style={{
                position: "relative",
                overflow: "hidden",
                background: "#0f0f0f",
                borderRight: "1px solid #222",
                borderRadius: 0,
                boxShadow: "none",
                padding: 20,
                minHeight: "calc(var(--mg-vh) * 100)",
                width: 440,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {mainGame.metadata.box_art && (
                <>
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage: `url(${mainGame.metadata.box_art})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transform: "scale(1.25)",
                      filter: "saturate(120%)",
                      opacity: 0.1,
                      zIndex: 0,
                    }}
                  />
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.60), rgba(0,0,0,0.82))",
                      zIndex: 0,
                    }}
                  />
                </>
              )}

              {/* Foreground content */}
              <div style={{ position: "relative", zIndex: 1 }}>
                {mainGame.metadata.box_art ? (
                  <img
                    src={mainGame.metadata.box_art}
                    alt={mainGame.metadata.name}
                    onError={(e) => (e.currentTarget.style.display = "none")}
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "cover",
                      borderRadius: 12,
                      marginBottom: 14,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.6)",
                    }}
                  />
                ) : null}

                <h1 style={{ fontSize: 26, margin: "2px 0 10px 0" }}>
                  {mainGame.metadata.name}
                </h1>

                <p
                  style={{
                    fontSize: 14,
                    color: "#ccc",
                    margin: "0 0 12px 0",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {mainGame.metadata.short_description || "No description available."}
                </p>

                <div style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 10 }}>
                  <div>
                    <b>Publisher:</b> {pub || "—"}
                  </div>
                  <div>
                    <b>Developer:</b> {dev || "—"}
                  </div>
                  <div>
                    <b>Release Date:</b>{" "}
                    {shortDateISO(mainGame.metadata.release_date) || "—"}
                  </div>
                  <div>
                    <b>Price:</b>{" "}
                    {mainGame.metadata.full_price != null &&
                    String(mainGame.metadata.full_price).trim() !== ""
                      ? `$${mainGame.metadata.full_price}`
                      : "—"}
                  </div>
                  <div>
                    <b>Tags:</b> {mainGame.metadata.tags || "—"}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {mainGame.metadata.video && (
                    <button onClick={() => setShowTrailer(true)} style={ghostBtn}>
                      <span aria-hidden>▶</span>
                      <span>Trailer</span>
                    </button>
                  )}
                  {mainGame.metadata.app_id && (
                    <a
                      href={`https://store.steampowered.com/app/${mainGame.metadata.app_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={ghostLink}
                      title="Open on Steam"
                    >
                      Steam
                    </a>
                  )}
                  {socialLinks.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ ...ghostLink, width: "auto", padding: "0 12px" }}
                      title={s.label}
                    >
                      {s.label}
                    </a>
                  ))}
                </div>
              </div>
            </aside>

            {/* Main content */}
            <main style={{ padding: "28px 56px 24px 24px" }}>
              {/* ===== Compact Multi-row Stats ===== */}
              <div
  style={{
    display: "grid",
    gridTemplateColumns:
      "40px minmax(180px, 260px) repeat(9, minmax(100px, 1fr))",
    gap: 12,
    alignItems: "center",
    // always keep a one-row buffer below the stats grid
    marginBottom: 12 + ROW_BUFFER_PX,
  }}
>

                  {/* Header row */}
  <div /> {/* X column */}
  <div /> {/* image + name column */}

  {/* mindSHARE (mindGAME) */}
  <div style={headerCellStyle}>
    <img
      src={`${process.env.PUBLIC_URL}/redesign/logos/icon/mindGAME Tree Logo.png`}
      alt="mindSHARE"
      style={headerIconStyle}
    />
    <span>mindSHARE</span>
  </div>

  {/* Steam – Players */}
  <div style={headerCellStyle}>
    <img
      src={`${process.env.PUBLIC_URL}/Steam Icon.png`}
      alt="Steam"
      style={headerIconStyle}
    />
    <span>Players</span>
  </div>

  {/* Steam – Rating (% positive) */}
  <div style={headerCellStyle}>
    <span>Rating</span>
  </div>

  {/* Steam – Est. DAU */}
  <div style={headerCellStyle}>
    <span>Est. DAU</span>
  </div>

  {/* Steam – Followers */}
  <div style={headerCellStyle}>
    <span>Followers</span>
  </div>

  {/* Google – Search Rank (7D) */}
  <div style={headerCellStyle}>
    <img
      src={`${process.env.PUBLIC_URL}/Google Icon.png`}
      alt="Google"
      style={headerIconStyle}
    />
    <span>Search Rank (7D)</span>
  </div>

  {/* YouTube – Video Rank (7D) */}
  <div style={headerCellStyle}>
    <img
      src={`${process.env.PUBLIC_URL}/YouTube Icon.png`}
      alt="YouTube"
      style={headerIconStyle}
    />
    <span>YouTube Rank (7D)</span>
  </div>

  {/* Twitch Rank */}
  <div style={headerCellStyle}>
    <img
      src={`${process.env.PUBLIC_URL}/Twitch Icon.png`}
      alt="Twitch"
      style={headerIconStyle}
    />
    <span>Twitch Rank</span>
  </div>

  {/* TikTok Views (7D) */}
  <div style={headerCellStyle}>
    <img
      src={`${process.env.PUBLIC_URL}/TikTok Icon.png`}
      alt="TikTok"
      style={headerIconStyle}
    />
    <span>TikTok Views (7D)</span>
  </div>



                {/* Data rows */}
                {statsRows.map((r, idx) => (
                  <div
                    key={r.key}
                    style={{
                      display: "contents",
                      animation: "fadeIn .25s ease-in forwards",
                      opacity: 0,
                    }}
                  >
                    {/* X column (only for rows 2+) */}
                    <div>
                      {idx > 0 && (
                        <button
                          onClick={() => removeComparedGame({ id: r.id, name: r.name })}
                          title={`Remove ${r.name}`}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            border: "1px solid #2a2a2a",
                            background: "#121212",
                            color: "#bbb",
                            fontWeight: 800,
                            cursor: "pointer",
                            lineHeight: 1,
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 10,
    position: "relative",
  }}
  onMouseEnter={() => setHoveredRowId(r.id)}
  onMouseLeave={() => setHoveredRowId((prev) => (prev === r.id ? null : prev))}
>
  {r.img ? (
    <img
      src={r.img}
      alt={r.name}
      width={84}
      height={42}
      style={{
        width: 84,
        height: 42,
        objectFit: "cover",
        borderRadius: 8,
        boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
        flexShrink: 0,
      }}
      onError={(e) => (e.currentTarget.style.display = "none")}
    />
  ) : (
    <div
      style={{
        width: 84,
        height: 42,
        borderRadius: 8,
        background: "#1b1b1b",
        border: "1px solid #222",
        flexShrink: 0,
      }}
    />
  )}

  <div style={{ fontWeight: 800, fontSize: 14 }}>{r.name}</div>

  {/* Hover popout – Steam-style info box */}
  {hoveredRowId === r.id && (
    <div
      style={{
        position: "absolute",
        top: "calc(100% + 10px)",
        left: 0,
        width: 320,
        background: "rgba(34,34,34,0.96)",
        borderRadius: 12,
        padding: "10px 12px",
        boxShadow: "0 12px 28px rgba(0,0,0,0.6)",
        border: "1px solid #333",
        zIndex: 5000,
        pointerEvents: "none",
        backdropFilter: "blur(10px)",
      }}
    >
      {/* Box art */}
      {r.img && (
        <img
          src={r.img}
          alt={r.name}
          style={{
            width: "100%",
            height: "auto",
            borderRadius: 10,
            display: "block",
            marginBottom: 10,
          }}
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      )}

      {/* Video embed (same behavior as Steam.jsx cards) */}
      {(() => {
        const embed = getVideoEmbed(r.videoUrl);
        if (!embed) return null;
        return (
          <div
            style={{
              width: "100%",
              aspectRatio: "16/9",
              background: "#000",
              borderRadius: 10,
              border: "1px solid #333",
              overflow: "hidden",
              marginBottom: 10,
            }}
          >
            {embed.type === "file" ? (
              <video
                src={embed.src}
                autoPlay
                muted
                loop
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  pointerEvents: "none",
                }}
              />
            ) : (
              <iframe
                src={embed.src}
                title={`${r.name} trailer`}
                frameBorder="0"
                allow="encrypted-media; picture-in-picture"
                allowFullScreen
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  pointerEvents: "none",
                }}
              />
            )}
          </div>
        );
      })()}

      {/* Description */}
      {r.short_description && (
        <div style={{ marginBottom: 10, color: "#fff", fontSize: 13 }}>
          {r.short_description}
        </div>
      )}

      {/* Release + Price */}
      {(r.release_date || r.full_price) && (
        <div style={{ marginBottom: 6, color: "#cfd8e3", fontSize: 13 }}>
          {r.release_date && (
            <div
              style={{
                marginBottom: formatPrice(r.full_price) ? 6 : 0,
              }}
            >
              <strong style={{ color: "#fff" }}>Release:</strong>{" "}
              {new Date(r.release_date).toLocaleDateString()}
            </div>
          )}
          {formatPrice(r.full_price) && (
            <div>
              <strong style={{ color: "#fff" }}>Price:</strong>{" "}
              {formatPrice(r.full_price)}
            </div>
          )}
        </div>
      )}

      {/* Publishers */}
      {r.publishers && (
        <div style={{ marginBottom: 6, color: "#cfd8e3", fontSize: 13 }}>
          <strong style={{ color: "#fff" }}>Publishers:</strong>{" "}
          {r.publishers}
        </div>
      )}

      {/* Developers */}
      {r.developers && (
        <div style={{ marginBottom: 10, color: "#cfd8e3", fontSize: 13 }}>
          <strong style={{ color: "#fff" }}>Developer(s):</strong>{" "}
          {r.developers}
        </div>
      )}

      {/* Tags */}
      {Array.isArray(r.tags) && r.tags.length > 0 && (
        <div style={{ color: "#cfd8e3", fontSize: 13 }}>
          <strong style={{ color: "#fff" }}>Tags:</strong>{" "}
          {r.tags.join(", ")}
        </div>
      )}
    </div>
  )}
</div>

                    <div style={topCellStyle(r.mindshare != null && r.mindshare === columnMax.mindshare)}>
                      {r.mindshare == null ? "—" : `${pct3(r.mindshare)}${r.mindshare_rank ? ` (#${r.mindshare_rank})` : ""}`}
                    </div>
<div
  style={topCellStyle(
    (r.current_ccu != null && r.current_ccu === columnMax.current_ccu) ||
    (r.peak_7d != null && r.peak_7d === columnMax.peak_7d) ||
    (r.all_time_peak != null && r.all_time_peak === columnMax.all_time_peak)
  )}
>
  <div style={{ fontWeight: 800, lineHeight: 1 }}>
    {num(r.current_ccu)}
  </div>
  <div style={{ fontSize: 12, color: "#cfd8e3", lineHeight: 1.15, marginTop: 2 }}>
    <span title="7-day peak">{num(r.peak_7d)}</span> <span style={{ opacity: 0.7 }}>7D</span>
    {"  ·  "}
    <span title="All-time peak">{num(r.all_time_peak)}</span> <span style={{ opacity: 0.7 }}>All</span>
  </div>
</div>


                    <div style={topCellStyle(r.positive_pct != null && r.positive_pct === columnMax.positive_pct)}>
                      {r.positive_pct == null ? "—" : `${Math.round(r.positive_pct)}%`}
                    </div>

                    <div style={topCellStyle(r.estimated_dau != null && r.estimated_dau === columnMax.estimated_dau)}>
                      {num(r.estimated_dau)}
                    </div>

                    <div style={topCellStyle(r.followers != null && r.followers === columnMax.followers)}>
                      {num(r.followers)}
                    </div>
<div style={topCellStyle(r.search_rank != null && r.search_rank === searchBestRank)}>
  {r.search_rank == null ? "—" : `#${r.search_rank}`}
</div>
<div style={topCellStyle(r.video_rank != null && r.video_rank === Number(youtubeBestRank))}>
  {r.video_rank == null ? "—" : `#${r.video_rank}`}
</div>

                    {/* NEW: Twitch Rank (no highlight, lower is better) */}
                    <div style={topCellStyle(r.twitch_rank != null && r.twitch_rank === twitchBestRank)}>
  {r.twitch_rank == null ? "—" : `#${r.twitch_rank}`}
</div>

                    <div style={topCellStyle(r.tiktok_views != null && r.tiktok_views === columnMax.tiktok_views)}>
                      {num(r.tiktok_views)}
                    </div>
                  </div>
                ))}
              </div>

              {/* quick keyframes for fade-in */}
              <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(-2px)}to{opacity:1;transform:none}}`}</style>

              {/* ===== Chart Toggle ===== */}
              <div
                role="tablist"
                aria-label="Data section"
                style={{
                  display: "inline-flex",
                  border: "1px solid #2a2a2a",
                  borderRadius: 999,
                  padding: 4,
                  gap: 6,
                  background: "#0b0b0b",
                  margin: "12px 0 18px 0",
                }}
              >
                <button
                  role="tab"
                  aria-selected={chartTab === "mindshare"}
                  onClick={() => setChartTab("mindshare")}
                  style={{
                    border: "none",
                    borderRadius: 999,
                    padding: "8px 14px",
                    fontWeight: 800,
                    cursor: "pointer",
                    background: chartTab === "mindshare" ? "#3fa9f5" : "transparent",
                    color: chartTab === "mindshare" ? "#000" : "#ddd",
                    transition: "background 0.15s ease",
                  }}
                >
                  mindSHARE
                </button>
                <button
                  role="tab"
                  aria-selected={chartTab === "players"}
                  onClick={() => setChartTab("players")}
                  style={{
                    border: "none",
                    borderRadius: 999,
                    padding: "8px 14px",
                    fontWeight: 800,
                    cursor: "pointer",
                    background: chartTab === "players" ? "#3fa9f5" : "transparent",
                    color: chartTab === "players" ? "#000" : "#ddd",
                    transition: "background 0.15s ease",
                  }}
                >
                  Player History
                </button>
                <button
                  role="tab"
                  aria-selected={chartTab === "reviews"}
                  onClick={() => setChartTab("reviews")}
                  style={{
                    border: "none",
                    borderRadius: 999,
                    padding: "8px 14px",
                    fontWeight: 800,
                    cursor: "pointer",
                    background: chartTab === "reviews" ? "#3fa9f5" : "transparent",
                    color: chartTab === "reviews" ? "#000" : "#ddd",
                    transition: "background 0.15s ease",
                  }}
                >
                  Steam Review History
                </button>

                <button
                  role="tab"
                  aria-selected={chartTab === "tiktok"}
                  onClick={() => setChartTab("tiktok")}
                  style={{
                    border: "none",
                    borderRadius: 999,
                    padding: "8px 14px",
                    fontWeight: 800,
                    cursor: "pointer",
                    background: chartTab === "tiktok" ? "#3fa9f5" : "transparent",
                    color: chartTab === "tiktok" ? "#000" : "#ddd",
                    transition: "background 0.15s ease",
                  }}
                >
                  TikTok History
                </button>

              </div>

              {/* ===== Player History ===== */}
              {chartTab === "players" && (
                <>
                  {/* Controls row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 12,
                      margin: "6px 0 12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 12,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          border: "1px solid #2a2a2a",
                          borderRadius: 8,
                          overflow: "hidden",
                        }}
                      >
                        <button
                          onClick={() => setDataMode("live")}
                          aria-pressed={dataMode === "live"}
                          style={{
                            height: 32,
                            padding: "0 12px",
                            background: dataMode === "live" ? "#3fa9f5" : "#111",
                            color: dataMode === "live" ? "#000" : "#ddd",
                            fontWeight: 700,
                            border: "none",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Live (7d)
                        </button>
                        <button
                          onClick={() => setDataMode("daily")}
                          aria-pressed={dataMode === "daily"}
                          style={{
                            height: 32,
                            padding: "0 12px",
                            background: dataMode === "daily" ? "#3fa9f5" : "#111",
                            color: dataMode === "daily" ? "#000" : "#ddd",
                            fontWeight: 700,
                            borderLeft: "1px solid #2a2a2a",
                            borderRight: "none",
                            borderTop: "none",
                            borderBottom: "none",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Daily
                        </button>
                      </div>

                      {/* Add-any-game dropdown (Steam/CCU compare) */}
                      <VirtualizedDropdownAsync
                        placeholder="Add any game…"
                        value={selectedGame}
                        onChange={async (opt) => {
                          if (opt) {
                            await addGameFromOption(opt);
                            setSelectedGame(null);
                          }
                        }}
                        cacheOptions
                        defaultOptions={[]}
                        filterOption={() => true}
                        isClearable
                        isSearchable
                        menuPosition="fixed"
                        width={"clamp(260px, calc(var(--mg-vw) * 32), 460px)"}
                        menuWidth={"100%"}
                        formatOptionLabel={(opt) => (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {opt.box_art ? (
                              <img
                                src={opt.box_art}
                                alt=""
                                width={20}
                                height={20}
                                style={{ width: 20, height: 20, objectFit: "cover", borderRadius: 4 }}
                                onError={(e) => (e.currentTarget.style.display = "none")}
                              />
                            ) : (
                              <div style={{ width: 20, height: 20, borderRadius: 4, background: "#222" }} />
                            )}
                            <span
                              style={{
                                flex: 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {opt.label}
                            </span>
                            <span style={{ opacity: 0.7, fontWeight: 800, fontSize: 14, paddingLeft: 6 }}>+</span>
                          </div>
                        )}
                        loadOptions={debounce((input, cb) => {
                          const q = (input || "").toLowerCase().trim();
                          if (!q) {
                            cb([]);
                            return;
                          }
                          const MAX = 120;
                          const starts = [];
                          const contains = [];
                          for (const g of gameOptions) {
                            if (String(g.app_id) === String(app_id)) continue;
                            if (g._lc.startsWith(q)) starts.push(g);
                            else if (g._lc.includes(q)) contains.push(g);
                            if (starts.length + contains.length >= MAX) break;
                          }
                          cb([...starts, ...contains].slice(0, MAX));
                        }, 200)}
                      />

                      {/* Live vs Daily specific toggle */}
{dataMode === "live" ? (
  <>
    <label
      style={{
        fontSize: 13,
        color: "#bbb",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
      title="Show/Hide APAC / EU / NA prime-hour bands"
    >
      <input
        type="checkbox"
        checked={showRegionBands}
        onChange={() => setShowRegionBands(v => !v)}
        style={{ marginRight: 6, verticalAlign: "middle" }}
      />
      View Region Prime Hours
    </label>

    {showRegionBands && (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginLeft: 10 }}>
        {/* APAC */}
        <span style={{
          display: "inline-block", width: 12, height: 12, borderRadius: 3,
          background: ASIA_BAND_FILL, boxShadow: "0 0 0 1px rgba(255,255,255,0.28) inset"
        }} />
        <span style={{ fontSize: 12, color: "#cfd8e3", marginRight: 12 }}>APAC</span>

        {/* EU */}
        <span style={{
          display: "inline-block", width: 12, height: 12, borderRadius: 3,
          background: EU_BAND_FILL, boxShadow: "0 0 0 1px rgba(255,255,255,0.28) inset"
        }} />
        <span style={{ fontSize: 12, color: "#cfd8e3", marginRight: 12 }}>EU</span>

        {/* NA */}
        <span style={{
          display: "inline-block", width: 12, height: 12, borderRadius: 3,
          background: NA_BAND_FILL, boxShadow: "0 0 0 1px rgba(255,255,255,0.28) inset"
        }} />
        <span style={{ fontSize: 12, color: "#cfd8e3", marginRight: 12 }}>NA</span>
      </div>
    )}
  </>
) : (
  <label
    style={{
      fontSize: 13,
      color: "#bbb",
      userSelect: "none",
      whiteSpace: "nowrap",
    }}
  >
    <input
      type="checkbox"
      checked={viewByDaysSinceLaunch}
      onChange={() => setViewByDaysSinceLaunch(v => !v)}
      style={{ marginRight: 6, verticalAlign: "middle" }}
    />
    View by Days Since Launch
  </label>
)}

                    </div>
                  </div>

                  {/* Chart */}
                  <div style={{ paddingRight: 40 }}>
                    {loadingCCU || !currentMainSeries || currentMainSeries.length === 0 ? (
                      <SkeletonChart
                        height={600}
                        message={loadingCCU ? "Loading CCU data..." : "No CCU data available"}
                      />
                    ) : (
                      <ResponsiveContainer width="100%" height={600}>
                        <AreaChart data={renderData}>
                          <CartesianGrid stroke="#222" />
                          <XAxis
                            dataKey="x"
                            stroke="#ddd"
                            tickFormatter={(tick) =>
                              dataMode === "live" ? fmtDateTime(tick) : String(tick)
                            }
                          />
                          <YAxis
                            stroke="#ddd"
                            width={110}
                            domain={[0, (max) => Math.ceil(max * 1.1)]}
                            allowDataOverflow
                            tickFormatter={(tick) => new Intl.NumberFormat("en-US").format(tick)}
                          />
                          <Tooltip
                            contentStyle={{ background: "#111", borderColor: "#333", color: "#fff" }}
                            labelFormatter={(label) =>
                              dataMode === "live" ? fmtDateTime(label) : String(label)
                            }
                          />

                          {(() => {
                            const peaks = {};
                            for (const row of renderData) {
                              for (const k of Object.keys(row)) {
                                if (k === "x") continue;
                                const v = Number(row[k]);
                                if (!Number.isFinite(v)) continue;
                                if (!peaks[k] || v > peaks[k].y) peaks[k] = { x: row.x, y: v };
                              }
                            }
                            return Object.entries(peaks).map(([id, { x, y }]) => (
                              <ReferenceDot
                                key={`peak-${id}`}
                                x={x}
                                y={y}
                                r={5}
                                ifOverflow="extendDomain"
                                fill={String(id) === String(mainGame.metadata.app_id) ? "#3fa9f5" : "#bbb"}
                                stroke="#000"
                                label={{
                                  value: appNames[id] || String(id),
                                  position: "top",
                                  fill: String(id) === String(mainGame.metadata.app_id) ? "#3fa9f5" : "#bbb",
                                  fontSize: 12,
                                }}
                              />
                            ));
                          })()}

                          {(() => {
                            let mainPeak = null;
                            for (const row of renderData) {
                              const v = Number(row[mainGame.metadata.app_id]);
                              if (!Number.isFinite(v)) continue;
                              if (!mainPeak || v > mainPeak.y) mainPeak = { x: row.x, y: v };
                            }
                            if (!mainPeak) return null;
                            return (
                              <ReferenceLine
                                y={mainPeak.y}
                                ifOverflow="extendDomain"
                                stroke="#3fa9f5"
                                strokeDasharray="4 4"
                                strokeWidth={1}
                                label={{
                                  value: `Peak: ${mainPeak.y.toLocaleString()} CCU`,
                                  position: "insideTopRight",
                                  fill: "#3fa9f5",
                                  fontSize: 12,
                                }}
                              />
                            );
                          })()}

                          {[mainGame, ...compareGames].map((g) => {
                            const isMain =
                              String(g.metadata.app_id) === String(mainGame.metadata.app_id);
                            const color = isMain ? "#3fa9f5" : "#999";
                            return (
                              <Area
                                key={g.metadata.app_id}
                                type="monotone"
                                dataKey={g.metadata.app_id}
                                stroke={color}
                                fill={color}
                                fillOpacity={isMain ? 0.3 : 0.15}
                                strokeWidth={isMain ? 2.2 : 1.4}
                                name={g.metadata.name}
                                dot={false}
                              />
                            );
                          })}
{dataMode === "live" && showRegionBands && renderData.length > 0 && (() => {
  const first = new Date(renderData[0].x).getTime();
  const last  = new Date(renderData[renderData.length - 1].x).getTime();

  const seenDays = new Set();
  const out = [];

  // helper to push a labeled, clamped band
  const pushBand = (x1ms, x2ms, fill, key, tag) => {
    const x1 = Math.max(first, x1ms);
    const x2 = Math.min(last,  x2ms);
    if (x2 <= x1) return;
    out.push(
      <ReferenceArea
        key={key}
        x1={new Date(x1).toISOString()}
        x2={new Date(x2).toISOString()}
        ifOverflow="hidden"
        fill={fill}
        stroke="none"
        label={{
          value: tag,                // "APAC" | "EU" | "NA"
          position: "top",
          fill: "#cfd8e3",
          fontSize: 10,
        }}
      />
    );
  };

  // one set of bands per UTC day in the window
  for (const row of renderData) {
    const d = new Date(row.x);
    if (Number.isNaN(d)) continue;

    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const day = d.getUTCDate();
    const dayKey = `${y}-${m}-${day}`;
    if (seenDays.has(dayKey)) continue;
    seenDays.add(dayKey);

    // APAC (no wrap)
    {
      const s = Date.UTC(y, m, day, ASIA_UTC_START_HOUR, 0, 0);
      const e = Date.UTC(y, m, day, ASIA_UTC_END_HOUR,   0, 0);
      pushBand(s, e, ASIA_BAND_FILL, `apac-${dayKey}`, "APAC");
    }

    // EU (no wrap)
    {
      const s = Date.UTC(y, m, day, EU_UTC_START_HOUR, 0, 0);
      const e = Date.UTC(y, m, day, EU_UTC_END_HOUR,   0, 0);
      pushBand(s, e, EU_BAND_FILL, `eu-${dayKey}`, "EU");
    }

    // NA (may wrap across midnight)
    if (NA_UTC_START_HOUR <= NA_UTC_END_HOUR) {
      const s = Date.UTC(y, m, day, NA_UTC_START_HOUR, 0, 0);
      const e = Date.UTC(y, m, day, NA_UTC_END_HOUR,   0, 0);
      pushBand(s, e, NA_BAND_FILL, `na-${dayKey}`, "NA");
    } else {
      // [start → 24:00) current day
      const s1 = Date.UTC(y, m, day, NA_UTC_START_HOUR, 0, 0);
      const e1 = Date.UTC(y, m, day, 24,                 0, 0);
      pushBand(s1, e1, NA_BAND_FILL, `na-a-${dayKey}`, "NA");
      // [00:00 → end) next day
      const s2 = Date.UTC(y, m, day + 1, 0,                0, 0);
      const e2 = Date.UTC(y, m, day + 1, NA_UTC_END_HOUR,  0, 0);
      pushBand(s2, e2, NA_BAND_FILL, `na-b-${dayKey}`, "NA");
    }
  }

  return out;
})()}


                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  {/* Range slider */}
                  {mergedData.length > 1 && (
                    <div style={{ marginTop: 14, paddingRight: 40 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 12,
                          color: "#bbb",
                          marginBottom: 6,
                        }}
                      >
                        <span>
                          Window: <b>{labelForIdx(winStartIdx)}</b>
                        </span>
                        <span>
                          → <b>{labelForIdx(winEndIdx)}</b>
                        </span>
                      </div>

                      <div style={{ position: "relative", height: 6, background: "#1a1a1a", borderRadius: 4 }}>
                        <div
                          style={{
                            position: "absolute",
                            left: `${(100 * winStartIdx) / (mergedData.length - 1)}%`,
                            width: `${
                              (100 * (winEndIdx - winStartIdx)) /
                              Math.max(1, mergedData.length - 1)
                            }%`,
                            top: 0,
                            bottom: 0,
                            background: "#3fa9f5",
                            opacity: 0.35,
                            borderRadius: 4,
                          }}
                        />
                      </div>

                      <div style={{ position: "relative", height: 0 }}>
                        <input
                          type="range"
                          min={0}
                          max={Math.max(0, mergedData.length - 1)}
                          value={winStartIdx}
                          onChange={(e) =>
                            setWinStartIdx(Math.min(parseInt(e.target.value, 10), winEndIdx - 1))
                          }
                          style={{
                            position: "relative",
                            width: "100%",
                            WebkitAppearance: "none",
                            background: "transparent",
                            top: -14,
                          }}
                        />
                        <input
                          type="range"
                          min={0}
                          max={Math.max(0, mergedData.length - 1)}
                          value={winEndIdx}
                          onChange={(e) =>
                            setWinEndIdx(Math.max(parseInt(e.target.value, 10), winStartIdx + 1))
                          }
                          style={{
                            position: "relative",
                            width: "100%",
                            WebkitAppearance: "none",
                            background: "transparent",
                            top: -26,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ===== Reviews Tab ===== */}
              {chartTab === "reviews" && (
                <div style={{ paddingRight: 40 }}>
                  {loadingReviews || !mainGame.reviews || mainGame.reviews.length === 0 ? (
                    <SkeletonChart
                      height={600}
                      message={loadingReviews ? "Loading reviews data..." : "No reviews data available"}
                    />
                  ) : (
                    <ResponsiveContainer width="100%" height={600}>
                      <AreaChart
                        data={mainGame.reviews.map((r) => ({
                          date: new Date(r.dt).toLocaleDateString(),
                          positive: r.positive,
                          negative: r.negative,
                        }))}
                      >
                        <CartesianGrid stroke="#222" />
                        <XAxis dataKey="date" stroke="#ccc" />
                        <YAxis stroke="#ccc" />
                        <Tooltip
                          contentStyle={{ backgroundColor: "#111", borderColor: "#333", color: "#fff" }}
                        />
                        <Area type="monotone" dataKey="positive" stroke="#3fa9f5" fill="rgba(63,169,245,0.25)" />
                        <Area type="monotone" dataKey="negative" stroke="#f31260" fill="rgba(243,18,96,0.18)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              )}

              {/* ===== TikTok Tab ===== */}
              {chartTab === "tiktok" && (
                <div style={{ paddingRight: 40 }}>
                  <div style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "center" }}>
                    <VirtualizedDropdownAsync
                      placeholder="Add any game from TikTok dataset…"
                      value={null}
                      onChange={async (opt) => {
                        if (opt) await addTikTokGame(opt);
                      }}
                      cacheOptions
                      defaultOptions={[]}
                      filterOption={() => true}
                      isClearable
                      isSearchable
                      menuPosition="fixed"
                      width={"clamp(280px, calc(var(--mg-vw) * 38), 520px)"}
                      menuWidth={"100%"}
                      formatOptionLabel={(opt) => (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {opt.imageUrl ? (
                            <img
                              src={opt.imageUrl}
                              alt=""
                              width={20}
                              height={20}
                              style={{ width: 20, height: 20, objectFit: "cover", borderRadius: 4 }}
                              onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                          ) : (
                            <div style={{ width: 20, height: 20, borderRadius: 4, background: "#222" }} />
                          )}
                          <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {opt.label}
                          </span>
                          <span style={{ opacity: 0.7, fontWeight: 800, fontSize: 14, paddingLeft: 6 }}>+</span>
                        </div>
                      )}
                      loadOptions={debounce((input, cb) => {
                        const q = (input || "").toLowerCase().trim();
                        if (!q) return cb([]);
                        const MAX = 200;
                        const starts = [];
                        const contains = [];
                        for (const g of tiktokGameOptions) {
                          if (g._lc.startsWith(q)) starts.push(g);
                          else if (g._lc.includes(q)) contains.push(g);
                          if (starts.length + contains.length >= MAX) break;
                        }
                        cb([...starts, ...contains].slice(0, MAX));
                      }, 200)}
                    />

                    <div
                      style={{
                        marginLeft: "auto",
                        display: "inline-flex",
                        border: "1px solid #2a2a2a",
                        borderRadius: 8,
                        overflow: "hidden",
                        background: "#0b0b0b",
                      }}
                    >
                      <button
                        onClick={() => setTikTokViewMode("lifetime")}
                        aria-pressed={tiktokViewMode === "lifetime"}
                        style={{
                          height: 28,
                          padding: "0 10px",
                          background: tiktokViewMode === "lifetime" ? "#3fa9f5" : "#111",
                          color: tiktokViewMode === "lifetime" ? "#000" : "#ddd",
                          fontWeight: 700,
                          border: "none",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Lifetime
                      </button>
                      <button
                        onClick={() => setTikTokViewMode("daily")}
                        aria-pressed={tiktokViewMode === "daily"}
                        style={{
                          height: 28,
                          padding: "0 10px",
                          background: tiktokViewMode === "daily" ? "#3fa9f5" : "#111",
                          color: tiktokViewMode === "daily" ? "#000" : "#ddd",
                          fontWeight: 700,
                          borderLeft: "1px solid #2a2a2a",
                          borderRight: "none",
                          borderTop: "none",
                          borderBottom: "none",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Daily
                      </button>
                    </div>
                  </div>

                  {loadingTikTok || tiktokGlobalLoading ? (
                    <SkeletonChart height={600} message={"Loading TikTok data..."} />
                  ) : tiktokSeries.length === 0 ? (
                    <SkeletonChart height={220} message={"No TikTok data found for this title."} />
                  ) : (
                    <ResponsiveContainer width="100%" height={600}>
                      <AreaChart data={tiktokCombinedData} margin={{ left: 10, right: 20, top: 0, bottom: 0 }}>
                        <CartesianGrid stroke="#222" />
                        <XAxis
                          dataKey="date"
                          stroke="#ccc"
                          tickFormatter={(v) => {
                            const d = new Date(v);
                            return isNaN(d)
                              ? v
                              : `${d.getMonth() + 1}/${String(d.getDate()).padStart(2, "0")}/${String(
                                  d.getFullYear()
                                ).slice(-2)}`;
                          }}
                        />
                        <YAxis
                          stroke="#ccc"
                          width={tiktokYAxisWidth}
                          tickFormatter={(v) => v.toLocaleString()}
                          domain={[0, (max) => Math.ceil((max || 0) * 1.1)]}
                        />
                        <Tooltip
                          contentStyle={{ background: "#111", borderColor: "#333", color: "#fff" }}
                          labelFormatter={(v) => {
                            const d = new Date(v);
                            return isNaN(d) ? v : d.toLocaleDateString();
                          }}
                          formatter={(value, name) => [
                            Number(value).toLocaleString(),
                            tiktokViewMode === "lifetime" ? `${name} • Views` : `${name} • Daily Views`,
                          ]}
                        />

                        {(() => {
                          const names = [];
                          if (mainGame?.metadata?.name && tiktokSeries.length) names.push(mainGame.metadata.name);
                          for (const g of tiktokCompare) if (g.series?.length) names.push(g.name);
                          return names.map((name, idx) => (
                            <Area
                              key={name}
                              type="monotone"
                              dataKey={name}
                              stroke={seriesColor(idx)}
                              fill={seriesColor(idx)}
                              fillOpacity={0.25}
                              strokeWidth={2}
                              dot={false}
                              name={name}
                            />
                          ));
                        })()}
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              )}

              {/* ===== mindSHARE Tab ===== */}
              {chartTab === "mindshare" && (
  <div style={{ paddingRight: 40 }}>
    {/* top row: Add game + Date/W2L toggle */}
    <div style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "center" }}>
      {/* NEW: mindSHARE add-any-game (same source as TikTok) */}
      <VirtualizedDropdownAsync
        placeholder="Add any game to mindSHARE…"
        value={null}
        onChange={async (opt) => { if (opt) await addMindshareGame(opt); }}
        cacheOptions
        defaultOptions={[]}
        filterOption={() => true}
        isClearable
        isSearchable
        menuPosition="fixed"
        width={"clamp(280px, calc(var(--mg-vw) * 38), 520px)"}
        menuWidth={"100%"}
        formatOptionLabel={(opt) => (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {opt.imageUrl ? (
              <img
                src={opt.imageUrl}
                alt=""
                width={20}
                height={20}
                style={{ width: 20, height: 20, objectFit: "cover", borderRadius: 4 }}
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <div style={{ width: 20, height: 20, borderRadius: 4, background: "#222" }} />
            )}
            <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {opt.label}
            </span>
            <span style={{ opacity: 0.7, fontWeight: 800, fontSize: 14, paddingLeft: 6 }}>+</span>
          </div>
        )}
        loadOptions={debounce((input, cb) => {
          const q = (input || "").toLowerCase().trim();
          if (!q) return cb([]);
          const MAX = 200;
          const starts = [];
          const contains = [];
          for (const g of tiktokGameOptions) {
            if (g._lc.startsWith(q)) starts.push(g);
            else if (g._lc.includes(q)) contains.push(g);
            if (starts.length + contains.length >= MAX) break;
          }
          cb([...starts, ...contains].slice(0, MAX));
        }, 200)}
      />

{mindshareXAxisMode === "w2l" && (
  <div
    style={{
      display: "inline-flex",
      border: "1px solid #2a2a2a",
      borderRadius: 8,
      overflow: "hidden",
      background: "#0b0b0b",
    }}
  >
    <button
      onClick={() => setMindshareAgg("weekly")}
      aria-pressed={mindshareAgg === "weekly"}
      style={{
        height: 28, padding: "0 10px",
        background: mindshareAgg === "weekly" ? "#3fa9f5" : "#111",
        color: mindshareAgg === "weekly" ? "#000" : "#ddd",
        fontWeight: 700, border: "none", cursor: "pointer", whiteSpace: "nowrap",
      }}
    >
      Weekly
    </button>
    <button
      onClick={() => setMindshareAgg("cumulative")}
      aria-pressed={mindshareAgg === "cumulative"}
      style={{
        height: 28, padding: "0 10px",
        background: mindshareAgg === "cumulative" ? "#3fa9f5" : "#111",
        color: mindshareAgg === "cumulative" ? "#000" : "#ddd",
        fontWeight: 700,
        borderLeft: "1px solid #2a2a2a", borderRight: "none", borderTop: "none", borderBottom: "none",
        cursor: "pointer", whiteSpace: "nowrap",
      }}
    >
      Cumulative
    </button>
  </div>
)}

      {/* Date / Weeks-to-Launch toggle group */}
      <div
        style={{
          marginLeft: "auto",
          display: "inline-flex",
          border: "1px solid #2a2a2a",
          borderRadius: 8,
          overflow: "hidden",
          background: "#0b0b0b",
        }}
      >
        <button
          onClick={() => setMindshareXAxisMode("date")}
          aria-pressed={mindshareXAxisMode === "date"}
          style={{
            height: 28, padding: "0 10px",
            background: mindshareXAxisMode === "date" ? "#3fa9f5" : "#111",
            color: mindshareXAxisMode === "date" ? "#000" : "#ddd",
            fontWeight: 700, border: "none", cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          Date
        </button>
        <button
          onClick={() => setMindshareXAxisMode("w2l")}
          aria-pressed={mindshareXAxisMode === "w2l"}
          style={{
            height: 28, padding: "0 10px",
            background: mindshareXAxisMode === "w2l" ? "#3fa9f5" : "#111",
            color: mindshareXAxisMode === "w2l" ? "#000" : "#ddd",
            fontWeight: 700,
            borderLeft: "1px solid #2a2a2a", borderRight: "none", borderTop: "none", borderBottom: "none",
            cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          Weeks to Launch
        </button>
      </div>
    </div>



                  {loadingMindshare ? (
                    <ChartImageLoader src={mainGame?.metadata?.box_art} height={600} />
                  ) : !mindshareSeries.length ? (
                    <ChartImageLoader src={mainGame?.metadata?.box_art} height={260} />
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={600}>
                        <AreaChart
  data={plotted} margin={chartMargin}>

                          <CartesianGrid stroke="#222" />
                          <XAxis
                            dataKey="x"
                            stroke="#ccc"
                            tickFormatter={(v) => {
                              if (mindshareXAxisMode === "w2l") return formatW2L(v); 
                              const d = new Date(v);
                              return isNaN(d)
                                ? String(v)
                                : `${d.getMonth() + 1}/${String(d.getDate()).padStart(2, "0")}/${String(
                                    d.getFullYear()
                                  ).slice(-2)}`;
                            }}
                          />
                          <YAxis
  stroke="#ccc"
  width={mindshareYAxisWidthDyn}
  tickFilter={null}
  tickFormatter={(v) => pct3(v)}
  domain={[
    0,
    () => {
      const hi = (mindshareXAxisMode === "w2l" && mindshareAgg === "cumulative")
        ? (msMaxCum || 0)
        : (msMaxWeeklyWindow || 0); // use windowed weekly max, not mindshareMaxVal
      return Math.max(0.000001, hi * 1.08);
    },
  ]}
/>
                          <Tooltip
                            contentStyle={{ background: "#111", borderColor: "#333", color: "#fff" }}
                            labelFormatter={(v) => {
                              if (mindshareXAxisMode === "w2l") return formatW2L(v);
                              const d = new Date(v);
                              return isNaN(d) ? String(v) : d.toLocaleDateString();
                            }}
                            formatter={(value, name) => [pct3(value), `${name} • mindSHARE`]}
                          />

                          {(() => {
                            // 1) Determine which series (game names) are on the chart now, and their color index
                            const names = [];
  if (mainGame?.metadata?.name && mindshareSeries.length) names.push(mainGame.metadata.name);
  for (const g of mindshareCompare) if (g.series?.length) names.push(g.name);

  // find peaks on the data we’re actually plotting
  const peaks = new Map(); // name -> { x, y, idx }
  const lastX = plotted.length ? plotted[plotted.length - 1].x : null;

  for (const row of plotted) {
    for (const name of names) {
      const v = Number(row[name]);
      if (!Number.isFinite(v)) continue;
      const prev = peaks.get(name);
      if (!prev || v > prev.y) {
        peaks.set(name, { x: row.x, y: v, idx: names.indexOf(name) });
      }
    }
  }

  return Array.from(peaks.entries()).map(([name, { x, y, idx }]) => {
    const atRightEdge = (lastX !== null && x === lastX);
    return (
      <ReferenceDot
        key={`ms-peak-${name}`}
        x={x}
        y={y}
        r={5}
        ifOverflow="extendDomain"
        fill={seriesColor(idx)}
        stroke="#000"
        label={{
          value: `${name} • ${pct3(y)}`,
          // shove left only in cumulative when the peak is on the last x
          position: atRightEdge ? "left" : "top",
          dx: atRightEdge ? -10 : 0,
          dy: atRightEdge ? 0 : -6,
          fill: seriesColor(idx),
          fontSize: 12,
          fontWeight: 700,
        }}
      />
    );
  });
})()}

                          {(() => {
                            const names = [];
                            if (mainGame?.metadata?.name && mindshareSeries.length) names.push(mainGame.metadata.name);
                            for (const g of mindshareCompare) if (g.series?.length) names.push(g.name);
                            return names.map((name, idx) => (
                              <Area
                                key={name}
                                type="monotone"
                                dataKey={name}
                                stroke={seriesColor(idx)}
                                fill={seriesColor(idx)}
                                fillOpacity={0.25}
                                strokeWidth={2}
                                dot={false}
                                name={name}
                              />
                            ));
                          })()}
                        </AreaChart>
                      </ResponsiveContainer>

                      {mindshareCombinedData.length > 1 && (
                        <div style={{ marginTop: 14, paddingRight: 40 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: 12,
                              color: "#bbb",
                              marginBottom: 6,
                            }}
                          >
                            <span>
                              Window: <b>{labelForMindIdx(mindWinStartIdx)}</b>
                            </span>
                            <span>
                              → <b>{labelForMindIdx(mindWinEndIdx)}</b>
                            </span>
                          </div>

                          <div style={{ position: "relative", height: 6, background: "#1a1a1a", borderRadius: 4 }}>
                            <div
                              style={{
                                position: "absolute",
                                left: `${(100 * mindWinStartIdx) / (mindshareCombinedData.length - 1)}%`,
                                width: `${
                                  (100 * (mindWinEndIdx - mindWinStartIdx)) /
                                  Math.max(1, mindshareCombinedData.length - 1)
                                }%`,
                                top: 0,
                                bottom: 0,
                                background: "#3fa9f5",
                                opacity: 0.35,
                                borderRadius: 4,
                              }}
                            />
                          </div>

                          <div style={{ position: "relative", height: 0 }}>
                            <input
                              type="range"
                              min={0}
                              max={Math.max(0, mindshareCombinedData.length - 1)}
                              value={mindWinStartIdx}
                              onChange={(e) =>
                                setMindWinStartIdx(Math.min(parseInt(e.target.value, 10), mindWinEndIdx - 1))
                              }
                              style={{
                                position: "relative",
                                width: "100%",
                                WebkitAppearance: "none",
                                background: "transparent",
                                top: -14,
                              }}
                            />
                            <input
                              type="range"
                              min={0}
                              max={Math.max(0, mindshareCombinedData.length - 1)}
                              value={mindWinEndIdx}
                              onChange={(e) =>
                                setMindWinEndIdx(Math.max(parseInt(e.target.value, 10), mindWinStartIdx + 1))
                              }
                              style={{
                                position: "relative",
                                width: "100%",
                                WebkitAppearance: "none",
                                background: "transparent",
                                top: -26,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </main>
          </div>

          {/* Trailer Modal */}
          {showTrailer && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0,0,0,0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
                padding: 40,
              }}
            >
              <div style={{ width: "80%", maxWidth: 960 }}>
                <button
                  onClick={() => setShowTrailer(false)}
                  style={{
                    marginBottom: 16,
                    background: "#3fa9f5",
                    color: "#000",
                    padding: "10px 16px",
                    border: "none",
                    borderRadius: 8,
                    fontWeight: "bold",
                    fontSize: 15,
                    cursor: "pointer",
                  }}
                >
                  ✕ Close Trailer
                </button>
                <video
                  src={mainGame.metadata.video}
                  autoPlay
                  controls
                  loop
                  style={{ width: "100%", borderRadius: 8, boxShadow: "0 0 20px rgba(0,0,0,0.6)" }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default GameDetail;
